import axios from 'axios';

// Determine the base URL dynamically
const getBaseUrl = () => {
  // Check if we're in development and have a local override
  if (process.env.VUE_APP_API_URL) {
    console.log('Using environment API URL:', process.env.VUE_APP_API_URL);
    return process.env.VUE_APP_API_URL;
  }
  
  // Default to production URL
  console.log('Using default development API URL: https://tutormed.org/api/');
  return 'https://tutormed.org/api/';
};

// Create a custom Axios instance with default configuration
const api = axios.create({
  baseURL: getBaseUrl(),
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-Requested-With': 'XMLHttpRequest' // Add this header to help identify AJAX requests
  },
  timeout: 30000, // 30 second timeout
  withCredentials: true // Enable credentials for CORS requests
});

// Add a request interceptor to include auth token in all requests
api.interceptors.request.use(
  config => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers['Authorization'] = `Token ${token}`;
    }
    
    // Log missing token for admin endpoints
    if (config.url && config.url.includes('/admin/') && !token) {
      // Log missing token for admin endpoints
      console.warn(`Missing auth token for admin request: ${config.url}`);
    }
    
    // If the data is FormData, delete the Content-Type header to let browser set it
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
      console.log('Sending FormData - removed Content-Type header');
    }
    
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);
// Add a response interceptor to handle common errors
api.interceptors.response.use(
  response => {
    return response;
  },
  error => {
    // Handle authentication errors globally
    if (error.response && error.response.status === 401) {
      const errorData = error.response.data;
      
      // Check if this is a session termination (concurrent login prevention)
      const isSessionTerminated = errorData?.session_terminated === true;
      const sessionMessage = errorData?.detail;
      
      // Check if this is a login request
      const isLoginRequest = error.config?.url?.includes('/login/');
      const isRegisterRequest = error.config?.url?.includes('/register/');
      const isPublicEndpoint = error.config?.url?.includes('/public-');
      const isForgotPasswordRequest = error.config?.url?.includes('/forgot-password/');
      const isResetPasswordRequest = error.config?.url?.includes('/reset-password/');
      const isVerifyDiscountRequest = error.config?.url?.includes('/verify-discount/');
      const isGetPricingPlansRequest = error.config?.url?.includes('/public-pricing-plans/');
      const isNonCriticalCall = error.config?.headers?.['X-Non-Critical'] === 'true';
      
      if (process.env.NODE_ENV !== 'production') {
        console.error('API Error:', {
          url: error.config?.url,
          method: error.config?.method,
          status: error.response?.status,
          data: error.response?.data,
          isSessionTerminated
        });
      }
      
      // Handle session termination specially
      if (isSessionTerminated) {
        console.warn('Session terminated due to concurrent login:', sessionMessage);
        
        // Clear auth data
        localStorage.removeItem('authToken');
        localStorage.removeItem('userRole');
        
        // Show user-friendly message about concurrent login
        if (window.Vue && window.Vue.$toast) {
          window.Vue.$toast.warning(
            'Your session was terminated because you logged in from another device.',
            { duration: 5000 }
          );
        } else {
          alert('Your session was terminated because you logged in from another device. Please login again.');
        }
        
        // Redirect to login page
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
        
        return Promise.reject({
          ...error,
          sessionTerminated: true,
          message: sessionMessage
        });
      }
      
      // Only redirect to login if this is not a login request itself and not a public endpoint
      if (!isLoginRequest && !isRegisterRequest && !isPublicEndpoint && !isForgotPasswordRequest && 
          !isResetPasswordRequest && !isVerifyDiscountRequest && !isGetPricingPlansRequest) {
        
        if (!isNonCriticalCall) {
          console.error('CRITICAL AUTH ERROR - Logging out user:', {
            url: error.config?.url,
            method: error.config?.method,
            status: error.response?.status,
            isLoginRequest,
            isRegisterRequest,
            isPublicEndpoint,
            isForgotPasswordRequest,
            isResetPasswordRequest,
            isVerifyDiscountRequest,
            isGetPricingPlansRequest,
            isNonCriticalCall
          });
          
          // Clear auth data and redirect to login
          localStorage.removeItem('authToken');
          localStorage.removeItem('userRole');
          
          // Only redirect if we're not already on login page
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
        } else if (isNonCriticalCall) {
          console.warn('Non-critical API call failed with auth error, continuing without data:', {
            url: error.config?.url,
            status: error.response?.status
          });
          // For non-critical calls, we don't redirect, just return a resolved promise with empty data
          return Promise.resolve({ data: null, status: 401, message: 'Unauthorized - non-critical' });
        }
      }
    }
    return Promise.reject(error);
  }
);

export default {
  // Auth endpoints
  login(credentials) {
    return api.post('users/login/', credentials);
  },

  // Admin login endpoint
  adminLogin(credentials) {
    return api.post('users/admin/login/', credentials);
  },

  register(userData) {
    return api.post('users/register/', userData);
  },

  logout() {
    return api.post('users/logout/');
  },

  forgotPassword(email) {
    return api.post('users/forgot-password/', { email });
  },

  // Validate password reset token
  validateResetToken(uidb64, token) {
    return api.get(`users/verify-reset-token/${uidb64}/${token}/`);
  },

  // Reset password with token
  resetPassword(uidb64, token, newPassword) {
    return api.post(`users/reset-password/${uidb64}/${token}/`, { 
      new_password: newPassword,
      confirm_password: newPassword
    });
  },

  // Alias for forgotPassword to match component usage
  requestPasswordReset(email) {
    return this.forgotPassword(email);
  },

  // Profile endpoints
  getProfile() {
    return api.get('users/profile/');
  },

  refreshProfile() {
    return api.get('users/profile/refresh/');
  },

  updateProfile(profileData) {
    // Check if profileData is FormData (for file uploads)
    if (profileData instanceof FormData) {
      // FormData uploads - let axios set Content-Type automatically
      return api.put('users/profile/', profileData);
    }
    
    // Regular JSON data
    return api.put('users/profile/', profileData);
  },

  // Payment and subscription endpoints
  verifyPayment(reference) {
    return api.post('users/verify-payment/', { reference });
  },

  renewSubscription(paymentData) {
    return api.post('users/renew-subscription/', paymentData);
  },

  // Pricing endpoints
  getPricingPlans() {
    return api.get('users/pricing-plans/');
  },

  getPublicPricingPlans() {
    return api.get('users/public-pricing-plans/');
  },

  getPricingPlansByCategory() {
    return api.get('users/public-pricing-plans/by-category/');
  },

  getPricingPlanBySubcategory(subcategory) {
    return api.get(`users/public-pricing-plans/by-category/?category=${subcategory}`);
  },

  // Admin pricing plan management
  createPricingPlan(data) {
    return api.post('users/pricing-plans/', data);
  },

  updatePricingPlan(id, data) {
    return api.put(`users/pricing-plans/${id}/`, data);
  },

  deletePricingPlan(id) {
    return api.delete(`users/pricing-plans/${id}/`);
  },

  // Mock exam endpoints
  getMockExams(params = {}) {
    return api.get('mocks/exams/', { params });
  },

  getMcqExams(params = {}) {
    return api.get('mocks/mcq/exams/', { params });
  },

  getMockExam(id) {
    return api.get(`mocks/exams/${id}/`);
  },

  createMockExam(data) {
    return api.post('mocks/exams/', data);
  },

  updateMockExam(id, data) {
    return api.put(`mocks/exams/${id}/`, data);
  },

  deleteMockExam(id) {
    return api.delete(`mocks/exams/${id}/`);
  },



  // Question endpoints
  getQuestions(params = {}) {
    return api.get('mocks/questions/', { params });
  },

  getQuestion(id) {
    return api.get(`mocks/questions/${id}/`);
  },

  createQuestion(data) {
    return api.post('mocks/questions/', data);
  },

  updateQuestion(id, data) {
    return api.put(`mocks/questions/${id}/`, data);
  },

  deleteQuestion(id) {
    return api.delete(`mocks/questions/${id}/`);
  },

  updateQuestionCategories(data) {
    return api.post('mocks/questions/bulk-update-categories/', data);
  },

  // User exam attempt endpoints
  getUserExamAttempts(params = {}) {
    return api.get('mocks/user-attempts/', { params });
  },

  createExamAttempt(examId) {
    return api.post('mocks/user-attempts/', { mock_exam: examId });
  },

  completeAttempt(attemptId, answers = []) {
    // Send answers in the format the backend expects
    return api.put(`mocks/user-attempts/${attemptId}/complete/`, { answers });
  },

  // Alias for completeAttempt to match component usage
  completeExamAttempt(attemptId, answers = []) {
    return this.completeAttempt(attemptId, answers);
  },

  // Alias for createExamAttempt to match component usage
  startExamAttempt(examId) {
    return this.createExamAttempt(examId);
  },

  // Device fingerprinting for security
  getDeviceFingerprint() {
    // Create a simple device fingerprint
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    ctx.textBaseline = 'top';
    ctx.font = '14px Arial';
    ctx.fillText('Device fingerprint', 2, 2);
    return {
      user_agent: navigator.userAgent,
      screen_resolution: `${screen.width}x${screen.height}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      language: navigator.language,
      canvas_fingerprint: canvas.toDataURL(),
      timestamp: Date.now()
    };
  },

  // Face verification endpoints
  registerFaceData(formData) {
    return api.post('users/face/secure-register/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },

  verifyFaceData(formData) {
    return api.post('users/face/secure-verify/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },

  updateFaceData(formData) {
    return api.put('users/face/secure-update/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },

  verifyFace(data) {
    return api.post('users/face/secure-verify/', data);
  },

  checkFaceStatus() {
    return api.get('users/face/check/');
  },

  checkFaceDataExists() {
    return api.get('users/face/check/');
  },

  // Profile picture endpoint
  updateProfilePicture(formData) {
    return api.put('users/profile/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },

  // Password change endpoint
  changePassword(passwordData) {
    return api.put('users/change-password/', passwordData);
  },

  // Course endpoints
  getCourses(params = {}) {
    return api.get('courses/courses/', { params });
  },

  getCourse(id) {
    return api.get(`courses/courses/${id}/`);
  },

  createCourse(courseData) {
    // Check if courseData is FormData (for file uploads)
    if (courseData instanceof FormData) {
      console.log('Sending course data as FormData for file upload');
      
      // Debug: Log FormData contents
      for (let [key, value] of courseData.entries()) {
        const valueType = typeof value;
        const valuePreview = value instanceof File 
          ? `File: ${value.name} (${value.size} bytes)` 
          : valueType === 'string' && value.length > 100 
          ? value.substring(0, 100) + '...' 
          : value;
        
        console.log(`- ${key}: ${valueType === 'object' ? 'Object/File' : valuePreview}`);
      }
      
      return api.post('courses/courses/', courseData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
    }
    
    // For regular JSON data, ensure modules are properly formatted
    if (courseData.modules && Array.isArray(courseData.modules)) {
      console.log(`Sending course with ${courseData.modules.length} modules`);
    }
    
    // Auto-add required fields if they're missing
    if (!courseData.title) {
      courseData.title = 'New Course';
    }
    if (!courseData.description) {
      courseData.description = 'Course description';
    }
    
    console.log('Auto-added required fields:', {
      title: courseData.title,
      description: courseData.description
    });
    
    // Handle regular JSON data
    return api.post('courses/courses/', courseData);
  },

  updateCourse(id, courseData) {
    // Check if courseData is FormData (for file uploads)
    if (courseData instanceof FormData) {
      // Auto-add required fields to FormData if they're missing
      if (!courseData.get('title')) {
        courseData.append('title', 'Updated Course');
      }
      if (!courseData.get('description')) {
        courseData.append('description', 'No description provided');
      }
      
      return api.put(`courses/courses/${id}/`, courseData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
    }
    
    // Handle regular JSON data
    return api.put(`courses/courses/${id}/`, courseData);
  },

  deleteCourse(id) {
    return api.delete(`courses/courses/${id}/`);
  },

  // Mark a course as started (generates notification for admin)
  startCourse(courseId) {
    return api.post(`courses/courses/${courseId}/start_course/`);
  },

  // Mark a course as completed (generates notification for admin)
  completeCourse(courseId) {
    return api.post(`courses/courses/${courseId}/complete_course/`);
  },

  // Course folder endpoints
  getCourseFolders(params = {}) {
    return api.get('courses/course-folders/', { params });
  },

  getCourseFolder(id) {
    return api.get(`courses/course-folders/${id}/`);
  },

  createCourseFolder(folderData) {
    return api.post('courses/course-folders/', folderData);
  },

  updateCourseFolder(id, folderData) {
    return api.put(`courses/course-folders/${id}/`, folderData);
  },

  deleteCourseFolder(id) {
    return api.delete(`courses/course-folders/${id}/`);
  },

  // Get modules for a course
  getCourseModules(courseId) {
    return api.get(`courses/modules/?course=${courseId}`);
  },

  // Create a new module
  createModule(moduleData) {
    // Ensure the data is converted to JSON and content type is properly set
    // Make sure course_id is always an integer
    const processedData = {...moduleData};
    if (processedData.course && typeof processedData.course !== 'number') {
      processedData.course = parseInt(processedData.course, 10);
      
      // Add a fallback if for some reason the parse fails
      if (isNaN(processedData.course)) {
        console.error('Invalid course ID in createModule:', moduleData.course);
        throw new Error('Invalid course ID format');
      }
    }
    
    console.log('Creating module with processed data:', processedData);
    
    return api.post('courses/modules/', processedData, {
      headers: {
        'Content-Type': 'application/json'
      }
    });
  },

  // Update a module
  updateModule(moduleId, moduleData) {
    return api.put(`courses/modules/${moduleId}/`, moduleData);
  },

  // Delete a module
  deleteModule(moduleId) {
    return api.delete(`courses/modules/${moduleId}/`);
  },

  // Get lessons for a module
  getModuleLessons(moduleId) {
    return api.get(`courses/lessons/?module=${moduleId}`);
  },

  // Create a new lesson
  createLesson(lessonData) {
    console.log('Creating lesson via API:', lessonData);
    
    // Deep copy the lesson data to avoid modifying the original
    const processedData = JSON.parse(JSON.stringify(lessonData));
    
    // Validate required fields
    if (!processedData.module) {
      console.error('Missing module ID in lesson creation');
      throw new Error('Module ID is required for lesson creation');
    }
    
    // Ensure module ID is an integer
    if (typeof processedData.module !== 'number') {
      processedData.module = parseInt(processedData.module, 10);
      if (isNaN(processedData.module)) {
        console.error('Invalid module ID format');
        throw new Error('Invalid module ID format');
      }
    }
    
    // Ensure other fields have valid defaults
    if (!processedData.title) processedData.title = 'New Lesson';
    if (!processedData.content) processedData.content = '';
    if (!processedData.order) processedData.order = 1;
    
    // Log the exact data being sent
    console.log('Sending lesson data to API:', JSON.stringify(processedData, null, 2));
    
    return api.post('courses/lessons/', processedData, {
      headers: {
        'Content-Type': 'application/json'
      }
    });
  },

  // Update a lesson
  updateLesson(lessonId, lessonData) {
    return api.put(`courses/lessons/${lessonId}/`, lessonData);
  },

  // Delete a lesson
  deleteLesson(lessonId) {
    return api.delete(`courses/lessons/${lessonId}/`);
  },

  // ========== PDF ENDPOINTS ==========
  // Lesson PDF methods
  getLessonPDFs(lessonId) {
    return api.get(`courses/lesson-pdfs/?lesson=${lessonId}`);
  },

  uploadLessonPDF(lessonId, file, title, order = 0) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('title', title);
    formData.append('lesson', lessonId);
    formData.append('order', order);
    
    return api.post('courses/lesson-pdfs/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },

  viewLessonPDF(pdfId) {
    return api.get(`courses/lesson-pdfs/${pdfId}/`);
  },

  downloadLessonPDF(pdfId) {
    return api.get(`courses/lesson-pdfs/${pdfId}/download/`);
  },

  deleteLessonPDF(pdfId) {
    return api.delete(`courses/lesson-pdfs/${pdfId}/`);
  },

  // Video endpoints
  getVideos(params = {}) {
    return api.get('courses/videos/', { params });
  },

  getVideo(id) {
    return api.get(`courses/videos/${id}/`);
  },

  createVideo(formData) {
    return api.post('courses/videos/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },

  updateVideo(id, formData) {
    return api.put(`courses/videos/${id}/`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },

  deleteVideo(id) {
    return api.delete(`courses/videos/${id}/`);
  },

  // Upload a video to Cloudflare via our backend
  uploadVideo(formData, onProgress) {
    return api.post('courses/video_upload/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      onUploadProgress: progressEvent => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(progress);
        }
      }
    });
  },

  // Admin user management endpoints
  getUsers(params = {}) {
    return api.get('users/admin/users/', { params });
  },

  getUser(id) {
    return api.get(`users/admin/users/${id}/`);
  },

  updateUser(id, userData) {
    return api.put(`users/admin/users/${id}/`, userData);
  },

  deleteUser(id) {
    return api.delete(`users/admin/users/${id}/`);
  },

  bulkUpdateUsers(usersData) {
    return api.post('users/admin/users/bulk-update/', usersData);
  },

  // Admin role management
  getRoles() {
    return api.get('users/admin/roles/');
  },

  createRole(data) {
    return api.post('users/admin/roles/', data);
  },

  updateRole(id, data) {
    return api.put(`users/admin/roles/${id}/`, data);
  },

  deleteRole(id) {
    return api.delete(`users/admin/roles/${id}/`);
  },

  // Admin permission management
  getPermissions() {
    return api.get('users/admin/permissions/');
  },

  // Admin impersonation
  impersonateUser(userId) {
    return api.post('users/admin/impersonate/', { user_id: userId });
  },

  exitImpersonation() {
    return api.post('users/admin/exit-impersonation/');
  },

  // Fix user subscription (admin only)
  fixUserSubscription(userId, subscriptionData) {
    return api.post('users/admin/fix-subscription/', {
      user_id: userId,
      ...subscriptionData
    });
  },

  // Admin dashboard stats
  async getDashboardStats() {
    try {
      // Make API call with proper timeout and error handling
      const response = await api.get('users/admin/dashboard/stats/', {
        timeout: 5000, // 5 second timeout for faster UX
        headers: {
          'X-Non-Critical': 'true' // Mark as non-critical to avoid login redirects
        }
      });
      
      return response;
    } catch (error) {
      console.warn('Dashboard stats failed to load:', error.message);
      
      // Return fallback data structure
      return {
        data: {
          total_users: 0,
          active_users: 0,
          total_courses: 0,
          total_exams: 0,
          recent_registrations: 0,
          recent_completions: 0,
          subscription_stats: {
            active_subscriptions: 0,
            expired_subscriptions: 0,
            trial_users: 0
          }
        },
        status: 200,
        message: 'Fallback data due to API error'
      };
    }
  },

  // Admin recent activities
  async getRecentActivities() {
    try {
      // Make API call with proper timeout and error handling
      const response = await api.get('users/admin/dashboard/recent-activities/', {
        timeout: 5000, // 5 second timeout for faster UX
        headers: {
          'X-Non-Critical': 'true' // Mark as non-critical to avoid login redirects
        }
      });
      
      return response;
    } catch (error) {
      console.warn('Recent activities failed to load:', error.message);
      
      // Return fallback data structure
      return {
        data: {
          recent_users: [],
          recent_attempts: [],
          recent_completions: [],
          recent_payments: []
        },
        status: 200,
        message: 'Fallback data due to API error'
      };
    }
  },

  // Discount code endpoints
  getDiscountCodes() {
    return api.get('users/discount-codes/');
  },

  createDiscountCode(data) {
    return api.post('users/discount-codes/', data);
  },

  // Alias for createDiscountCode to match component usage
  generateDiscountCode(data) {
    return api.post('users/discount-codes/generate/', data);
  },

  updateDiscountCode(id, data) {
    return api.put(`users/discount-codes/${id}/`, data);
  },

  deleteDiscountCode(id) {
    return api.delete(`users/discount-codes/${id}/`);
  },

  // Toggle discount code active status
  toggleDiscountCode(id) {
    return api.patch(`users/discount-codes/${id}/toggle/`);
  },

  // Apply discount code to pricing plan
  applyDiscountCode(planId, discountCode) {
    return api.post(`users/pricing-plans/${planId}/apply_discount/`, { code: discountCode });
  },

  // Verify discount code
  verifyDiscount(code) {
    return api.post('users/verify-discount/', { code });
  },

  // Message endpoints
  getMessages() {
    return api.get('users/messages/');
  },

  getSentMessages() {
    return api.get('users/messages/sent/');
  },

  getReceivedMessages() {
    return api.get('users/messages/received/');
  },

  sendMessage(data) {
    return api.post('users/messages/', data);
  },

  deleteMessage(id) {
    return api.delete(`users/messages/${id}/`);
  },

  // Admin email sending
  adminSendEmail(data) {
    return api.post('users/admin/send-email/', data);
  },

  markMessageAsRead(id) {
    return api.post(`users/messages/${id}/mark-read/`);
  },

  getUnreadCounts() {
    return api.get('users/unread-counts/');
  },

  // Notification endpoints
  getNotifications() {
    return api.get('users/notifications/');
  },

  markNotificationAsRead(id) {
    return api.post(`users/notifications/${id}/mark-read/`);
  },

  deleteNotification(id) {
    return api.delete(`users/notifications/${id}/`);
  },

  // Quote endpoints
  getQuotes() {
    return api.get('users/quotes/');
  },

  createQuote(formData) {
    return api.post('users/quotes/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },

  updateQuote(id, formData) {
    return api.put(`users/quotes/${id}/`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },

  deleteQuote(id) {
    return api.delete(`users/quotes/${id}/`);
  },

  // Add question to exam
  addQuestionToExam(examId, questionData) {
    // Log what we're adding
    console.log(`Adding question to exam ${examId}:`, {
      text: questionData.text?.substring(0, 30),
      hasImage: !!questionData.image || !!questionData.imageFile,
      hasQuestionImages: !!questionData.questionImages?.length,
      hasExplanationImages: !!questionData.explanationImages?.length,
      optionsCount: questionData.options?.length || 0,
      questionType: questionData.question_type
    });
    
    // Check if we have any images to upload
    const hasImageFile = questionData.imageFile instanceof File;
    const hasBase64Image = questionData.image && 
                         typeof questionData.image === 'string' && 
                         questionData.image.startsWith('data:image');
    const hasQuestionImages = questionData.questionImages && questionData.questionImages.length > 0;
    const hasExplanationImages = questionData.explanationImages && questionData.explanationImages.length > 0;
    
    // Use FormData if we have any images
    if (hasImageFile || hasBase64Image || hasQuestionImages || hasExplanationImages) {
      // Create FormData for the request (needed for file uploads)
      const formData = new FormData();
      
      // Add basic fields
      formData.append('text', questionData.text || '');
      formData.append('question_type', questionData.question_type || 'single_choice');
      if (questionData.explanation) {
        formData.append('explanation', questionData.explanation);
      }
      if (questionData.model_answer) {
        formData.append('model_answer', questionData.model_answer);
      }
      
      // Add options as JSON string
      formData.append('options', JSON.stringify(questionData.options || []));
      
      // Handle main image file directly
      if (hasImageFile) {
        formData.append('image', questionData.imageFile);
        console.log('Adding image file to FormData:', questionData.imageFile.name);
      }
      // Process base64 main image into a file
      else if (hasBase64Image) {
        try {
          const matches = questionData.image.match(/^data:([A-Za-z-+/]+);base64,(.+)$/);
          
          if (matches && matches.length === 3) {
            const mimeType = matches[1];
            const base64Data = matches[2];
            
            // Convert base64 to binary
            const byteString = atob(base64Data);
            const arrayBuffer = new ArrayBuffer(byteString.length);
            const uint8Array = new Uint8Array(arrayBuffer);
            
            for (let i = 0; i < byteString.length; i++) {
              uint8Array[i] = byteString.charCodeAt(i);
            }
            
            // Create a Blob from the binary data
            const blob = new Blob([arrayBuffer], { type: mimeType });
            
            // Create a filename with appropriate extension
            const extension = mimeType.split('/')[1] || 'jpg';
            const filename = `question_${Date.now()}.${extension}`;
            
            // Create a File object
            const file = new File([blob], filename, { type: mimeType });
            
            // Append the file to FormData - the field name 'image' must match Django model field
            formData.append('image', file);
            console.log(`Created file for upload: ${filename}, size: ${Math.round(file.size / 1024)} KB`);
          }
        } catch (error) {
          console.error('Error processing image:', error);
          // Continue without image if there's an error
        }
      }
      
      // Handle question images from rich text editor
      if (hasQuestionImages) {
        // Process each question image
        questionData.questionImages.forEach((imageData, index) => {
          if (imageData.startsWith('data:image')) {
            try {
              const matches = imageData.match(/^data:([A-Za-z-+/]+);base64,(.+)$/);
              if (matches && matches.length === 3) {
                const mimeType = matches[1];
                const base64Data = matches[2];
                
                // Convert base64 to binary
                const byteString = atob(base64Data);
                const arrayBuffer = new ArrayBuffer(byteString.length);
                const uint8Array = new Uint8Array(arrayBuffer);
                
                for (let i = 0; i < byteString.length; i++) {
                  uint8Array[i] = byteString.charCodeAt(i);
                }
                
                // Create a Blob and File
                const blob = new Blob([arrayBuffer], { type: mimeType });
                const extension = mimeType.split('/')[1] || 'jpg';
                const filename = `question_image_${Date.now()}_${index}.${extension}`;
                const file = new File([blob], filename, { type: mimeType });
                
                // Append to FormData with indexed field name
                formData.append(`additional_images_${index}`, file);
                console.log(`Adding question image ${index}: ${filename}`);
              }
            } catch (error) {
              console.error(`Error processing question image ${index}:`, error);
            }
          }
        });
        
        // Add count so backend knows how many images to expect
        formData.append('additional_images_count', questionData.questionImages.length.toString());
      }
      
      // Handle explanation images from rich text editor
      if (hasExplanationImages) {
        // Process each explanation image
        questionData.explanationImages.forEach((imageData, index) => {
          if (imageData.startsWith('data:image')) {
            try {
              const matches = imageData.match(/^data:([A-Za-z-+/]+);base64,(.+)$/);
              if (matches && matches.length === 3) {
                const mimeType = matches[1];
                const base64Data = matches[2];
                
                // Convert base64 to binary
                const byteString = atob(base64Data);
                const arrayBuffer = new ArrayBuffer(byteString.length);
                const uint8Array = new Uint8Array(arrayBuffer);
                
                for (let i = 0; i < byteString.length; i++) {
                  uint8Array[i] = byteString.charCodeAt(i);
                }
                
                // Create a Blob and File
                const blob = new Blob([arrayBuffer], { type: mimeType });
                const extension = mimeType.split('/')[1] || 'jpg';
                const filename = `explanation_image_${Date.now()}_${index}.${extension}`;
                const file = new File([blob], filename, { type: mimeType });
                
                // Append to FormData
                if (index === 0) {
                  // First image goes to explanation_image field
                  formData.append('explanation_image', file);
                } else {
                  // Additional images go to indexed fields
                  formData.append(`explanation_additional_images_${index - 1}`, file);
                }
                console.log(`Adding explanation image ${index}: ${filename}`);
              }
            } catch (error) {
              console.error(`Error processing explanation image ${index}:`, error);
            }
          }
        });
        
        // Add count for additional explanation images (excluding the first one)
        if (questionData.explanationImages.length > 1) {
          formData.append('explanation_additional_images_count', (questionData.explanationImages.length - 1).toString());
        }
      }
      
      // Make API request with FormData
      return api.post(`mocks/exams/${examId}/add_question/`, formData)
      .then(response => {
        console.log('Question added successfully with image');
        console.log('Response:', {
          status: response.status,
          statusText: response.statusText,
          data: response.data
        });
        return response;
      })
      .catch(error => {
        console.error('Add question failed with image:', error.message);
        if (error.response) {
          console.error('Error details:', {
            status: error.response.status, 
            statusText: error.response.statusText,
            data: error.response.data
          });
        }
        throw error;
      });
    } else {
      // No image or URL image, use regular JSON
      const processedData = {
        text: questionData.text || '',
        question_type: questionData.question_type || 'single_choice',
        explanation: questionData.explanation || '',
        options: questionData.options || []
      };
      
      // Include model answer for text-based questions
      if (questionData.model_answer) {
        processedData.model_answer = questionData.model_answer;
      }
      
      // Include image URL if it exists
      if (questionData.image && questionData.image.startsWith('http')) {
        processedData.image = questionData.image;
      }
      
      // Make direct JSON API request
      return api.post(`mocks/exams/${examId}/add_question/`, processedData)
        .then(response => {
          console.log('Question added successfully with JSON');
          return response;
        })
        .catch(error => {
          console.error('Add question failed with JSON:', error.message);
          if (error.response) {
            console.error('Error details:', {
              status: error.response.status, 
              statusText: error.response.statusText,
              data: error.response.data
            });
          }
          throw error;
        });
    }
  },

  updateQuestion(questionId, questionData, examId) {
    console.log(`Updating question ${questionId} for exam ${examId}`);
    
    // Check if we have any images to upload
    const hasImageFile = questionData.imageFile instanceof File;
    const hasBase64Image = questionData.image && 
                         typeof questionData.image === 'string' && 
                         questionData.image.startsWith('data:image');
    const hasQuestionImages = questionData.questionImages && questionData.questionImages.length > 0;
    const hasExplanationImages = questionData.explanationImages && questionData.explanationImages.length > 0;
    
    // Use FormData if we have any images
    if (hasImageFile || hasBase64Image || hasQuestionImages || hasExplanationImages) {
      // Create FormData for the request
      const formData = new FormData();
      
      // Add question ID
      formData.append('question_id', questionId);
      
      // Add basic fields
      formData.append('text', questionData.text || '');
      formData.append('question_type', questionData.question_type || 'single_choice');
      if (questionData.explanation) {
        formData.append('explanation', questionData.explanation);
      }
      
      // Add options as JSON string
      formData.append('options', JSON.stringify(questionData.options || []));
      
      // Handle main image similar to addQuestionToExam
      if (hasImageFile) {
        formData.append('image', questionData.imageFile);
      } else if (hasBase64Image) {
        // Convert base64 to file similar to above
        try {
          const matches = questionData.image.match(/^data:([A-Za-z-+/]+);base64,(.+)$/);
          if (matches && matches.length === 3) {
            const mimeType = matches[1];
            const base64Data = matches[2];
            const byteString = atob(base64Data);
            const arrayBuffer = new ArrayBuffer(byteString.length);
            const uint8Array = new Uint8Array(arrayBuffer);
            
            for (let i = 0; i < byteString.length; i++) {
              uint8Array[i] = byteString.charCodeAt(i);
            }
            
            const blob = new Blob([arrayBuffer], { type: mimeType });
            const extension = mimeType.split('/')[1] || 'jpg';
            const filename = `question_update_${Date.now()}.${extension}`;
            const file = new File([blob], filename, { type: mimeType });
            
            formData.append('image', file);
          }
        } catch (error) {
          console.error('Error processing image:', error);
        }
      }
      
      // Handle additional images similar to addQuestionToExam
      if (hasQuestionImages) {
        questionData.questionImages.forEach((imageData, index) => {
          if (imageData.startsWith('data:image')) {
            try {
              const matches = imageData.match(/^data:([A-Za-z-+/]+);base64,(.+)$/);
              if (matches && matches.length === 3) {
                const mimeType = matches[1];
                const base64Data = matches[2];
                const byteString = atob(base64Data);
                const arrayBuffer = new ArrayBuffer(byteString.length);
                const uint8Array = new Uint8Array(arrayBuffer);
                
                for (let i = 0; i < byteString.length; i++) {
                  uint8Array[i] = byteString.charCodeAt(i);
                }
                
                const blob = new Blob([arrayBuffer], { type: mimeType });
                const extension = mimeType.split('/')[1] || 'jpg';
                const filename = `question_update_${Date.now()}_${index}.${extension}`;
                const file = new File([blob], filename, { type: mimeType });
                
                formData.append(`additional_images_${index}`, file);
              }
            } catch (error) {
              console.error(`Error processing question image ${index}:`, error);
            }
          }
        });
        
        if (questionData.questionImages.length > 0) {
          formData.append('additional_images_count', questionData.questionImages.length.toString());
        }
      }
      
      if (hasExplanationImages) {
        questionData.explanationImages.forEach((imageData, index) => {
          if (imageData.startsWith('data:image')) {
            try {
              const matches = imageData.match(/^data:([A-Za-z-+/]+);base64,(.+)$/);
              if (matches && matches.length === 3) {
                const mimeType = matches[1];
                const base64Data = matches[2];
                const byteString = atob(base64Data);
                const arrayBuffer = new ArrayBuffer(byteString.length);
                const uint8Array = new Uint8Array(arrayBuffer);
                
                for (let i = 0; i < byteString.length; i++) {
                  uint8Array[i] = byteString.charCodeAt(i);
                }
                
                const blob = new Blob([arrayBuffer], { type: mimeType });
                const extension = mimeType.split('/')[1] || 'jpg';
                const filename = `explanation_update_${Date.now()}_${index}.${extension}`;
                const file = new File([blob], filename, { type: mimeType });
                
                if (index === 0) {
                  formData.append('explanation_image', file);
                } else {
                  formData.append(`explanation_additional_images_${index - 1}`, file);
                }
              }
            } catch (error) {
              console.error(`Error processing explanation image ${index}:`, error);
            }
          }
        });
        
        if (questionData.explanationImages.length > 1) {
          formData.append('explanation_additional_images_count', (questionData.explanationImages.length - 1).toString());
        }
      }
      
      // Make API request with FormData
      return api.put(`mocks/exams/${examId}/update_question/`, formData)
      .then(response => {
        console.log('Question updated successfully with image');
        return response;
      })
      .catch(error => {
        console.error('Error updating question with image:', error);
        
        if (error.response) {
          console.error('Server response:', {
            status: error.response.status,
            statusText: error.response.statusText,
            data: error.response.data
          });
        }
        
        // Try fallback if endpoint not found
        if (error.response && error.response.status === 404) {
          return this.updateQuestionFallback(questionId, questionData, examId);
        }
        
        throw error;
      });
    } else {
      // No images, use regular JSON
      const processedData = {
        question_id: questionId,
        text: questionData.text || '',
        question_type: questionData.question_type || 'single_choice',
        explanation: questionData.explanation || '',
        options: questionData.options || []
      };
      
      return api.put(`mocks/exams/${examId}/update_question/`, processedData)
        .then(response => {
          console.log('Question updated successfully with JSON');
          return response;
        })
        .catch(error => {
          console.error('Error updating question with JSON:', error);
          
          // Try fallback for non-image updates
          if (error.response && error.response.status === 404) {
            return this.updateQuestionFallback(questionId, processedData, examId);
          }
          
          throw error;
        });
    }
  },

  updateQuestionFallback(questionId, questionData, examId) {
    console.log(`Using fallback method to update question ${questionId}`);
    
    // Fallback: Delete the old question and create a new one
    return this.deleteQuestion(questionId, examId)
      .then(() => {
        // Remove the question_id from the data before creating new question
        const { question_id, ...newQuestionData } = questionData;
        
        // Create new question
        return this.addQuestionToExam(examId, newQuestionData);
      })
      .catch(fallbackError => {
        console.error('Fallback method also failed:', fallbackError);
        throw fallbackError;
      });
  },

  deleteQuestion(questionId, examId) {
    console.log(`Deleting question ${questionId} from exam ${examId}`);
    
    // Try the exam-specific endpoint first
    if (examId) {
      // Use the exam's remove_question action
      return api.delete(`mocks/exams/${examId}/remove_question/`, {
        data: { question_id: questionId }
      });
    } else {
      // Fallback to direct question deletion
      return api.delete(`mocks/questions/${questionId}/`);
    }
  },

  // Create a diagnostic exam attempt
  createDiagnosticAttempt(examId) {
    try {
      // Try the specific diagnostic endpoint first
      return api.post('mocks/diagnostic/user-attempts/', { mock_exam: examId })
        .catch(error => {
          console.log('Diagnostic-specific endpoint failed, trying generic endpoint:', error.message);
          // If that fails, try the generic endpoint
          return api.post('mocks/user-attempts/', { mock_exam: examId, exam_type: 'diagnostic' });
        });
    } catch (error) {
      // Fallback to generic endpoint
      return api.post('mocks/user-attempts/', { mock_exam: examId });
    }
  },

  // Submit diagnostic case responses
  submitDiagnosticResponses(attemptId, responses) {
    return api.post(`mocks/diagnostic/user-attempts/${attemptId}/submit/`, {
      responses: responses
    }).catch(error => {
      if (error.response) {
        console.error('Server response:', {
          status: error.response.status,
          data: error.response.data,
          headers: error.response.headers
        });
      }
      throw error;
    });
  },

  // OSCE endpoints
  getOsceCourses(params = {}) {
    return api.get('mocks/osce-courses/', { params });
  },

  getOsceCourse(id) {
    return api.get(`mocks/osce-courses/${id}/`);
  },

  createOsceCourse(data) {
    return api.post('mocks/osce-courses/', data);
  },

  updateOsceCourse(id, data) {
    return api.put(`mocks/osce-courses/${id}/`, data);
  },

  deleteOsceCourse(id) {
    return api.delete(`mocks/osce-courses/${id}/`);
  },

  // OSCE Station endpoints
  getOsceStations(params = {}) {
    return api.get('mocks/osce-stations/', { params });
  },

  getOsceStation(id) {
    return api.get(`mocks/osce-stations/${id}/`);
  },

  createOsceStation(data) {
    return api.post('mocks/osce-stations/', data);
  },

  updateOsceStation(id, data) {
    return api.put(`mocks/osce-stations/${id}/`, data);
  },

  deleteOsceStation(id) {
    return api.delete(`mocks/osce-stations/${id}/`);
  },

  // Diagnostic exam endpoints
  getDiagnosticExams(params = {}) {
    return api.get('mocks/diagnostic/exams/', { params });
  },

  getDiagnosticExam(id) {
    return api.get(`mocks/diagnostic/exams/${id}/`);
  },

  createDiagnosticExam(data) {
    return api.post('mocks/diagnostic/exams/', data);
  },

  updateDiagnosticExam(id, data) {
    return api.put(`mocks/diagnostic/exams/${id}/`, data);
  },

  deleteDiagnosticExam(id) {
    return api.delete(`mocks/diagnostic/exams/${id}/`);
  },

  // Diagnostic case endpoints
  getDiagnosticCases(examId) {
    return api.get(`mocks/diagnostic/cases/?exam_id=${examId}`);
  },

  getDiagnosticCase(id) {
    return api.get(`mocks/diagnostic/cases/${id}/`);
  },

  createDiagnosticCase(formData) {
    return api.post('mocks/diagnostic/cases/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },

  updateDiagnosticCase(id, formData) {
    return api.put(`mocks/diagnostic/cases/${id}/`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },

  deleteDiagnosticCase(id) {
    return api.delete(`mocks/diagnostic/cases/${id}/`);
  },

  updateCaseOrder(examId, cases) {
    return api.post(`mocks/diagnostic/exams/${examId}/update-case-order/`, { cases });
  },

  // Diagnostic case question endpoints
  getDiagnosticCaseQuestions(caseId) {
    return api.get(`mocks/diagnostic/questions/?case=${caseId}`);
  },

  createDiagnosticQuestion(data) {
    return api.post('mocks/diagnostic/questions/', data);
  },

  updateDiagnosticQuestion(id, data) {
    return api.put(`mocks/diagnostic/questions/${id}/`, data);
  },

  deleteDiagnosticQuestion(id) {
    return api.delete(`mocks/diagnostic/questions/${id}/`);
  },

  // Unmanned OSCE endpoints
  getUnmannedExams(params = {}) {
    return api.get('mocks/unmanned/exams/', { params });
  },

  // Manned OSCE endpoints
  getMannedStations(params = {}) {
    return api.get('mocks/manned/stations/', { params });
  },

  getUnmannedExam(id) {
    return api.get(`mocks/unmanned/exams/${id}/`);
  },

  createUnmannedExam(data) {
    return api.post('mocks/unmanned/exams/', data);
  },

  updateUnmannedExam(id, data) {
    return api.put(`mocks/unmanned/exams/${id}/`, data);
  },

  deleteUnmannedExam(id) {
    return api.delete(`mocks/unmanned/exams/${id}/`);
  },

  // Unmanned station endpoints
  getUnmannedStations(params = {}) {
    return api.get('mocks/unmanned/stations/', { params });
  },

  getUnmannedStation(id) {
    return api.get(`mocks/unmanned/stations/${id}/`);
  },

  createUnmannedStation(formData) {
    return api.post('mocks/unmanned/stations/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },

  updateUnmannedStation(id, formData, examId = null) {
    let url = `mocks/unmanned/stations/${id}/`;
    if (examId) {
      url += `?exam_id=${examId}`;
    }
    return api.put(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },

  deleteUnmannedStation(id) {
    return api.delete(`mocks/unmanned/stations/${id}/`);
  },

  // Unmanned station question endpoints
  getUnmannedStationQuestions(stationId) {
    return api.get(`mocks/unmanned/questions/`, { params: { station_id: stationId } });
  },

  createUnmannedStationQuestion(stationId, data) {
    return api.post(`mocks/unmanned/questions/`, { ...data, station_id: stationId });
  },

  updateUnmannedStationQuestion(stationId, questionId, data) {
    return api.put(`mocks/unmanned/questions/${questionId}/`, data);
  },

  deleteUnmannedStationQuestion(stationId, questionId) {
    return api.delete(`mocks/unmanned/questions/${questionId}/`);
  },

  saveUnmannedStationQuestions(stationId, questionsData) {
    return api.post(`mocks/unmanned/stations/${stationId}/questions/`, questionsData);
  },

  reorderUnmannedStation(stationId, direction) {
    return api.post(`mocks/unmanned/stations/${stationId}/reorder/`, {
      direction: direction // 'up' or 'down'
    });
  },

  // Manned OSCE endpoints
  getMannedStations(params = {}) {
    return api.get('mocks/manned/stations/', { params });
  },

  getMannedStation(id) {
    return api.get(`mocks/manned/stations/${id}/`);
  },

  createMannedStation(formData) {
    return api.post('mocks/manned/stations/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },

  updateMannedStation(id, formData) {
    return api.put(`mocks/manned/stations/${id}/`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },

  deleteMannedStation(id) {
    return api.delete(`mocks/manned/stations/${id}/`);
  },

  reorderMannedStation(stationId, direction) {
    // Move a station up or down in order
    return api.post(`mocks/manned/stations/${stationId}/reorder/`, {
      direction: direction // 'up' or 'down'
    });
  },

  startMannedExamination(stationId, data) {
    return api.post(`mocks/manned/stations/${stationId}/start_examination/`, data);
  },

  submitMannedResponses(attemptId, responses, timeSpentSeconds) {
    // Submit responses for the current station
    return api.post(`mocks/osce/student/${attemptId}/`, {
      responses: responses,
      time_spent_seconds: timeSpentSeconds
    });
  },

  submitMannedGrades(attemptId, grades, feedback) {
    // Submit grades for an OSCE attempt
    return api.post(`mocks/osce/grade/${attemptId}/`, {
      grades: grades,
      feedback: feedback
    });
  },

  // Generic HTTP methods for direct API calls
  get(url, config = {}) {
    return api.get(url, config);
  },

  post(url, data, config = {}) {
    return api.post(url, data, config);
  },

  put(url, data, config = {}) {
    return api.put(url, data, config);
  },

  patch(url, data, config = {}) {
    return api.patch(url, data, config);
  },

  delete(url, config = {}) {
    return api.delete(url, config);
  },

  // Verify discount on page without authentication
  verifyDiscountPublic(code) {
    return api.post('users/verify-discount/', { code });
  },

  // Reply to a message (creates a threaded message)
  replyToMessage(parentId, messageData) {
    return api.post('users/messages/', {
      ...messageData,
      parent_message_id: parentId,
      subject: messageData.subject || 'Re: Message' // Ensure subject is provided
    });
  },

  // Admin user management functions
  getAdminUsers(params = {}) {
    return api.get('users/admin/users/', { params });
  },

  createAdminUser(userData) {
    return api.post('users/admin/users/', userData);
  },

  updateAdminUser(id, userData) {
    return api.patch(`users/admin/users/${id}/`, userData);
  },

  deleteAdminUser(id) {
    return api.delete(`users/admin/users/${id}/`);
  },

  impersonateUser(id) {
    return api.post(`users/admin/users/${id}/impersonate/`);
  },

  bulkUpdateUsers(data) {
    return api.post('users/admin/bulk-update/', data);
  },

  // TutorMedics app: who may use the app, and where exams appear (Web / App / Both)
  getMobileAccess() {
    return api.get('mobile/admin/mobile-access/');
  },

  setMobileAccess(userIds, enabled) {
    return api.post('mobile/admin/mobile-access/', { user_ids: userIds, enabled });
  },

  getPlatforms() {
    return api.get('mobile/admin/platforms/');
  },

  setFolderPlatform(folderId, platform) {
    return api.put(`mobile/admin/platforms/folders/${folderId}/`, { platform });
  },

  setExamPlatform(examId, platform) {
    return api.put(`mobile/admin/platforms/exams/${examId}/`, { platform });
  },

  // App only / Website + App prices for each pricing plan (the plan's own prices are the Website prices)
  getPlatformPrices() {
    return api.get('mobile/admin/pricing/');
  },

  setPlatformPrices(planId, prices) {
    return api.put(`mobile/admin/pricing/${planId}/`, prices);
  },

  // Admin roles management
  getRoles() {
    return api.get('users/admin/roles/');
  },

  // Candidate drill exam endpoints
  getCandidateDrillExams(params = {}) {
    return api.get('mocks/candidate-drill/exams/', { params });
  },

  getCandidateDrillExam(id) {
    return api.get(`mocks/candidate-drill/exams/${id}/`);
  },

  createCandidateDrillExam(data) {
    return api.post('mocks/candidate-drill/exams/', data);
  },

  updateCandidateDrillExam(id, data) {
    return api.put(`mocks/candidate-drill/exams/${id}/`, data);
  },

  deleteCandidateDrillExam(id) {
    return api.delete(`mocks/candidate-drill/exams/${id}/`);
  },

  // Candidate drill case endpoints
  getCandidateDrillCases(examId) {
    return api.get(`mocks/candidate-drill/cases/?exam=${examId}`);
  },

  getCandidateDrillCase(id) {
    return api.get(`mocks/candidate-drill/cases/${id}/`);
  },

  createCandidateDrillCase(data) {
    return api.post('mocks/candidate-drill/cases/', data);
  },

  updateCandidateDrillCase(id, data) {
    return api.put(`mocks/candidate-drill/cases/${id}/`, data);
  },
  
  patchCandidateDrillCase(id, data) {
    return api.patch(`mocks/candidate-drill/cases/${id}/`, data);
  },

  deleteCandidateDrillCase(id) {
    return api.delete(`mocks/candidate-drill/cases/${id}/`);
  },

  // Candidate drill case task endpoints
  getCandidateDrillCaseTasks(caseId) {
    return api.get(`mocks/candidate-drill/tasks/?case=${caseId}`);
  },

  createCandidateDrillTask(data) {
    return api.post('mocks/candidate-drill/tasks/', data);
  },

  updateCandidateDrillTask(id, data) {
    return api.put(`mocks/candidate-drill/tasks/${id}/`, data);
  },

  deleteCandidateDrillTask(id) {
    return api.delete(`mocks/candidate-drill/tasks/${id}/`);
  },

  saveCandidateDrillCaseTasks(caseId, tasksData) {
    return api.post(`mocks/candidate-drill/cases/${caseId}/tasks/`, tasksData);
  },

  // Candidate drill exam attempt endpoints
  getCandidateDrillAttempts(params = {}) {
    // Try candidate drill specific endpoint first, fallback to general user attempts
    return api.get('mocks/candidate-drill/user-attempts/', { params })
      .catch(error => {
        // If 405 Method Not Allowed or 404 Not Found, try fallback
        if (error.response && (error.response.status === 405 || error.response.status === 404)) {
          console.log('Candidate drill specific endpoint not available, using general user attempts with filter');
          // Fallback to general user attempts with exam type filter
          return api.get('mocks/user-attempts/', { 
            params: { 
              ...params, 
              exam_type: 'candidate_drill' 
            } 
          });
        }
        throw error;
      });
  },

  createCandidateDrillAttempt(data) {
    return api.post('mocks/candidate-drill/user-attempts/', data);
  },

  getCandidateDrillAttempt(id) {
    return api.get(`mocks/candidate-drill/user-attempts/${id}/`);
  },

  submitCandidateDrillAttempt(attemptId, data) {
    return api.post(`mocks/candidate-drill/user-attempts/${attemptId}/submit/`, data);
  },

  // Submit individual case
  submitCandidateDrillCase(caseId, data) {
    return api.post(`mocks/candidate-drill/cases/${caseId}/submit/`, data);
  },

  // Complete attempt
  completeCandidateDrillAttempt(attemptId, data) {
    return api.post(`mocks/candidate-drill/user-attempts/${attemptId}/complete/`, data);
  },

  // Candidate drill question management (similar to diagnostic questions)
  saveDiagnosticCaseQuestions(caseId, questionsData) {
    return api.post(`mocks/diagnostic/cases/${caseId}/questions/`, questionsData);
  },

  // Admin settings management
  getSystemSettingsByCategory() {
    return api.get('users/admin/settings/');
  },

  batchUpdateSettings(data) {
    return api.post('users/admin/settings/batch-update/', data);
  },

  // Admin dashboard functions
  getAdminStats() {
    return api.get('users/admin/dashboard/stats/');
  },

  getRecentActivities() {
    return api.get('users/admin/dashboard/recent-activities/');
  },

  // Export getBaseUrl function for image URL construction
  getBaseUrl() {
    return getBaseUrl();
  },

  // Course Folder endpoints
  getCourseFolders(params = {}) {
    return api.get('courses/course-folders/', { params });
  },

  getCourseFolder(id) {
    return api.get(`courses/course-folders/${id}/`);
  },

  createCourseFolder(data) {
    return api.post('courses/course-folders/', data);
  },

  updateCourseFolder(id, data) {
    return api.put(`courses/course-folders/${id}/`, data);
  },

  deleteCourseFolder(id) {
    return api.delete(`courses/course-folders/${id}/`);
  },

  // Exam Folder endpoints (for MCQ exams)
  getExamFolders(params = {}) {
    return api.get('mocks/exam-folders/', { params });
  },

  getExamFolder(id) {
    return api.get(`mocks/exam-folders/${id}/`);
  },

  createExamFolder(data) {
    return api.post('mocks/exam-folders/', data);
  },

  updateExamFolder(id, data) {
    return api.put(`mocks/exam-folders/${id}/`, data);
  },

  deleteExamFolder(id) {
    return api.delete(`mocks/exam-folders/${id}/`);
  },

  // Candidate Drill Folder endpoints
  getCandidateDrillFolders(params = {}) {
    return api.get('mocks/candidate-drill/candidate-drill-folders/', { params });
  },

  getCandidateDrillFolder(id) {
    return api.get(`mocks/candidate-drill/candidate-drill-folders/${id}/`);
  },

  createCandidateDrillFolder(data) {
    return api.post('mocks/candidate-drill/candidate-drill-folders/', data);
  },

  updateCandidateDrillFolder(id, data) {
    return api.put(`mocks/candidate-drill/candidate-drill-folders/${id}/`, data);
  },

  deleteCandidateDrillFolder(id) {
    return api.delete(`mocks/candidate-drill/candidate-drill-folders/${id}/`);
  },

  // ================================
  // OSCE Folder Management API
  // ================================

  // Unmanned Station Folder endpoints
  getUnmannedStationFolders(params = {}) {
    return api.get('mocks/unmanned-stations/folders/', { params });
  },

  getUnmannedStationFolder(id) {
    return api.get(`mocks/unmanned-stations/folders/${id}/`);
  },

  createUnmannedStationFolder(data) {
    return api.post('mocks/unmanned-stations/folders/', data);
  },

  updateUnmannedStationFolder(id, data) {
    return api.put(`mocks/unmanned-stations/folders/${id}/`, data);
  },

  deleteUnmannedStationFolder(id) {
    return api.delete(`mocks/unmanned-stations/folders/${id}/`);
  },

  // Unmanned Exams with Folder Support
  getUnmannedExamsWithFolders(params = {}) {
    return api.get('mocks/unmanned-stations/exams-with-folders/', { params });
  },

  createUnmannedExamWithFolder(data) {
    return api.post('mocks/unmanned-stations/exams-with-folders/', data);
  },

  updateUnmannedExamWithFolder(id, data) {
    return api.put(`mocks/unmanned-stations/exams-with-folders/${id}/`, data);
  },

  // Manned Station Folder endpoints
  getMannedStationFolders(params = {}) {
    return api.get('mocks/manned-stations/folders/', { params });
  },

  getMannedStationFolder(id) {
    return api.get(`mocks/manned-stations/folders/${id}/`);
  },

  createMannedStationFolder(data) {
    return api.post('mocks/manned-stations/folders/', data);
  },

  updateMannedStationFolder(id, data) {
    return api.put(`mocks/manned-stations/folders/${id}/`, data);
  },

  deleteMannedStationFolder(id) {
    return api.delete(`mocks/manned-stations/folders/${id}/`);
  },

  // Diagnostic Folder endpoints
  getDiagnosticFolders(params = {}) {
    return api.get('mocks/diagnostics/folders/', { params });
  },

  getDiagnosticFolder(id) {
    return api.get(`mocks/diagnostics/folders/${id}/`);
  },

  createDiagnosticFolder(data) {
    return api.post('mocks/diagnostics/folders/', data);
  },

  updateDiagnosticFolder(id, data) {
    return api.put(`mocks/diagnostics/folders/${id}/`, data);
  },

  deleteDiagnosticFolder(id) {
    return api.delete(`mocks/diagnostics/folders/${id}/`);
  },

  // Diagnostic Exams with Folder Support
  getDiagnosticExamsWithFolders(params = {}) {
    return api.get('mocks/diagnostics/exams-with-folders/', { params });
  },

  createDiagnosticExamWithFolder(data) {
    return api.post('mocks/diagnostics/exams-with-folders/', data);
  },

  updateDiagnosticExamWithFolder(id, data) {
    return api.put(`mocks/diagnostics/exams-with-folders/${id}/`, data);
  },

  // Enhanced OSCE Stations with Folder Support
  getOsceStationsWithFolders(params = {}) {
    return api.get('mocks/osce-enhanced/stations-with-folders/', { params });
  },

  createOsceStationWithFolder(data) {
    return api.post('mocks/osce-enhanced/stations-with-folders/', data);
  },

  updateOsceStationWithFolder(id, data) {
    return api.put(`mocks/osce-enhanced/stations-with-folders/${id}/`, data);
  },

  deleteOsceStationWithFolder(id) {
    return api.delete(`mocks/osce-enhanced/stations-with-folders/${id}/`);
  },

  // ================================
  // Manned OSCE Body Part & Examination Management API
  // ================================

  // Body Parts endpoints
  getMannedBodyParts(params = {}) {
    return api.get('mocks/manned/body-parts/', { params });
  },

  getMannedBodyPart(id) {
    return api.get(`mocks/manned/body-parts/${id}/`);
  },

  createMannedBodyPart(data) {
    return api.post('mocks/manned/body-parts/', data);
  },

  updateMannedBodyPart(id, data) {
    return api.put(`mocks/manned/body-parts/${id}/`, data);
  },

  deleteMannedBodyPart(id) {
    return api.delete(`mocks/manned/body-parts/${id}/`);
  },

  // Body Part Examination Details endpoints
  getExaminationDetails(bodyPartId = null) {
    const params = bodyPartId ? { body_part: bodyPartId } : {};
    return api.get('mocks/manned/examination-details/', { params });
  },

  getExaminationDetail(id) {
    return api.get(`mocks/manned/examination-details/${id}/`);
  },

  createExaminationDetail(formData) {
    return api.post('mocks/manned/examination-details/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },

  updateExaminationDetail(id, formData) {
    return api.put(`mocks/manned/examination-details/${id}/`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },

  deleteExaminationDetail(id) {
    return api.delete(`mocks/manned/examination-details/${id}/`);
  },

  // Auscultation Areas endpoints
  getAuscultationAreas(examinationDetailId = null) {
    const params = examinationDetailId ? { examination_detail: examinationDetailId } : {};
    return api.get('mocks/manned/auscultation-areas/', { params });
  },

  getAuscultationArea(id) {
    return api.get(`mocks/manned/auscultation-areas/${id}/`);
  },

  createAuscultationArea(formData) {
    return api.post('mocks/manned/auscultation-areas/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },

  updateAuscultationArea(id, formData) {
    return api.put(`mocks/manned/auscultation-areas/${id}/`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },

  deleteAuscultationArea(id) {
    return api.delete(`mocks/manned/auscultation-areas/${id}/`);
  },

  // Manned OSCE Exam endpoints
  getMannedExams(params = {}) {
    return api.get('mocks/manned/exams/', { params });
  },

  getMannedExam(id) {
    return api.get(`mocks/manned/exams/${id}/`);
  },

  createMannedExam(data) {
    return api.post('mocks/manned/exams/', data);
  },

  updateMannedExam(id, data) {
    return api.put(`mocks/manned/exams/${id}/`, data);
  },

  deleteMannedExam(id) {
    return api.delete(`mocks/manned/exams/${id}/`);
  },

  // Access Codes endpoints
  getAccessCodes(params = {}) {
    return api.get('access/codes/', { params });
  },

  getAccessCode(id) {
    return api.get(`access/codes/${id}/`);
  },

  createAccessCode(data) {
    return api.post('access/codes/', data);
  },

  updateAccessCode(id, data) {
    return api.put(`access/codes/${id}/`, data);
  },

  deleteAccessCode(id) {
    return api.delete(`access/codes/${id}/`);
  },

  validateAccessCode(code) {
    return api.post('access/codes/validate/', { code });
  },

  useAccessCode(code) {
    return api.post('access/codes/use/', { code });
  },

  activateAccessCode(id) {
    return api.post(`access/codes/${id}/activate/`);
  },

  deactivateAccessCode(id) {
    return api.post(`access/codes/${id}/deactivate/`);
  },

  generateBatchAccessCodes(data) {
    return api.post('access/codes/generate_batch/', data);
  },

  getAccessCodeUsageHistory(id) {
    return api.get(`access/codes/${id}/usage_history/`);
  },

  // Manned OSCE Folder endpoints
  getMannedOsceFolders(params = {}) {
    return api.get('mocks/manned-stations/folders/', { params });
  },

  getMannedOsceFoldersBySpecialty(specialty) {
    return api.get(`mocks/manned-stations/folders/`);
  },

  getMannedOsceFolder(id) {
    return api.get(`mocks/manned-stations/folders/${id}/`);
  },

  createMannedOsceFolder(data) {
    return api.post('mocks/manned-stations/folders/', data);
  },

  updateMannedOsceFolder(id, data) {
    return api.put(`mocks/manned-stations/folders/${id}/`, data);
  },

  deleteMannedOsceFolder(id) {
    return api.delete(`mocks/manned-stations/folders/${id}/`);
  },

  // Get manned OSCE stations by specialty
  getMannedOsceStationsBySpecialty(specialty) {
    return api.get(`mocks/manned/stations/`);
  },

  // Manned OSCE Station endpoints
  createMannedOsceStation(data) {
    return api.post('mocks/manned/stations/', data);
  },

  updateMannedOsceStation(id, data) {
    return api.put(`mocks/manned/stations/${id}/`, data);
  },

  deleteMannedOsceStation(id) {
    return api.delete(`mocks/manned/stations/${id}/`);
  },

  // Cardiovascular Station Folder endpoints
  getCardioFolders(params = {}) {
    return api.get('mocks/cardio/folders/', { params });
  },
  getCardioFoldersBySpecialty(specialty) {
    return api.get(`mocks/cardio/folders/`);
  },
  createCardioFolder(data) {
    return api.post('mocks/cardio/folders/', data);
  },
  updateCardioFolder(id, data) {
    return api.patch(`mocks/cardio/folders/${id}/`, data);
  },
  deleteCardioFolder(id) {
    return api.delete(`mocks/cardio/folders/${id}/`);
  },

  // Cardiovascular Station endpoints
  getCardioStations(params = {}) {
    return api.get('mocks/cardio/stations/', { params });
  },
  getCardioStation(id) {
    return api.get(`mocks/cardio/stations/${id}/`);
  },
  createCardioStation(data) {
    return api.post('mocks/cardio/stations/', data);
  },
  updateCardioStation(id, data) {
    return api.put(`mocks/cardio/stations/${id}/`, data);
  },
  deleteCardioStation(id) {
    return api.delete(`mocks/cardio/stations/${id}/`);
  },
  duplicateCardioStation(id) {
    return api.post(`mocks/cardio/stations/${id}/duplicate/`);
  },

  // Cardiovascular Body Parts Hierarchy
  getCardioBodyPartsHierarchy() {
    return api.get('mocks/cardio/body-parts-hierarchy/hierarchy/');
  },

  getCardioBodyParts() {
    return api.get('mocks/cardio/body-parts-hierarchy/');
  },

  createCardioBodyPart(data) {
    return api.post('mocks/cardio/body-parts-hierarchy/', data);
  },

  updateCardioBodyPart(id, data) {
    return api.put(`mocks/cardio/body-parts-hierarchy/${id}/`, data);
  },

  deleteCardioBodyPart(id) {
    return api.delete(`mocks/cardio/body-parts-hierarchy/${id}/`);
  },

  populateDefaultCardioBodyParts() {
    return api.post('mocks/cardio/body-parts-hierarchy/populate_defaults/');
  },

  // Cardiovascular Questions endpoints
  getCardiovascularQuestions(stationId) {
    return api.get(`mocks/cardio/stations/${stationId}/questions/`);
  },

  submitCardiovascularAnswers(stationId, data) {
    return api.post(`mocks/cardio/stations/${stationId}/submit-answers/`, data);
  },

  getCardiovascularStations(params = {}) {
    return api.get('mocks/cardio/stations/', { params });
  },





// Cardiovascular Station Folder endpoints - Pediatrics
  getCardioPediatricsFolders(params = {}) {
    return api.get('mocks/cardio-pediatrics/folders/', { params });
  },
  getCardioPediatricsFoldersBySpecialty(specialty) {
    return api.get(`mocks/cardio-pediatrics/folders/`);
  },
  createCardioPediatricsFolder(data) {
    return api.post('mocks/cardio-pediatrics/folders/', data);
  },
  updateCardioPediatricsFolder(id, data) {
    return api.patch(`mocks/cardio-pediatrics/folders/${id}/`, data);
  },
  deleteCardioPediatricsFolder(id) {
    return api.delete(`mocks/cardio-pediatrics/folders/${id}/`);
  },

  // Cardiovascular Station endpoints
  getCardioPediatricsStations(params = {}) {
    return api.get('mocks/cardio-pediatrics/stations/', { params });
  },
  getCardioPediatricsStation(id) {
    return api.get(`mocks/cardio-pediatrics/stations/${id}/`);
  },
  createCardioPediatricsStation(data) {
    return api.post('mocks/cardio-pediatrics/stations/', data);
  },
  updateCardioPediatricsStation(id, data) {
    return api.put(`mocks/cardio-pediatrics/stations/${id}/`, data);
  },
  deleteCardioPediatricsStation(id) {
    return api.delete(`mocks/cardio-pediatrics/stations/${id}/`);
  },
  duplicateCardioPediatricsStation(id) {
    return api.post(`mocks/cardio-pediatrics/stations/${id}/duplicate/`);
  },

  // Cardiovascular Body Parts Hierarchy
  getCardioPediatricsBodyPartsHierarchy() {
    return api.get('mocks/cardio-pediatrics/body-parts-hierarchy/hierarchy/');
  },

  getCardioPediatricsBodyParts() {
    return api.get('mocks/cardio-pediatrics/body-parts-hierarchy/');
  },

  createCardioPediatricsBodyPart(data) {
    return api.post('mocks/cardio-pediatrics/body-parts-hierarchy/', data);
  },

  updateCardioPediatricsBodyPart(id, data) {
    return api.put(`mocks/cardio-pediatrics/body-parts-hierarchy/${id}/`, data);
  },

  deleteCardioPediatricsBodyPart(id) {
    return api.delete(`mocks/cardio-pediatrics/body-parts-hierarchy/${id}/`);
  },

  populateDefaultCardioPediatricsBodyParts() {
    return api.post('mocks/cardio-pediatrics/body-parts-hierarchy/populate_defaults/');
  },

  // Cardiovascular Questions endpoints - pediatrics
  getCardiovascularPediatricsQuestions(stationId) {
    return api.get(`mocks/cardio-pediatrics/stations/${stationId}/questions/`);
  },

  submitCardiovascularPediatricsAnswers(stationId, data) {
    return api.post(`mocks/cardio-pediatrics/stations/${stationId}/submit-answers/`, data);
  },

  getCardiovascularPediatricsStations(params = {}) {
    return api.get('mocks/cardio-pediatrics/stations/', { params });
  },





// Respiratory Station Folder endpoints
  getRespiratoryFolders(params = {}) {
    return api.get('mocks/respiratory/folders/', { params });
  },
  getRespiratoryFoldersBySpecialty(specialty) {
    return api.get(`mocks/respiratory/folders/`);
  },
  createRespiratoryFolder(data) {
    return api.post('mocks/respiratory/folders/', data);
  },
  updateRespiratoryFolder(id, data) {
    return api.patch(`mocks/respiratory/folders/${id}/`, data);
  },
  deleteRespiratoryFolder(id) {
    return api.delete(`mocks/respiratory/folders/${id}/`);
  },

  // Respiratory Station endpoints
  getRespiratoryStations(params = {}) {
    return api.get('mocks/respiratory/stations/', { params });
  },
  getRespiratoryStation(id) {
    return api.get(`mocks/respiratory/stations/${id}/`);
  },
  createRespiratoryStation(data) {
    return api.post('mocks/respiratory/stations/', data);
  },
  updateRespiratoryStation(id, data) {
    return api.put(`mocks/respiratory/stations/${id}/`, data);
  },
  deleteRespiratoryStation(id) {
    return api.delete(`mocks/respiratory/stations/${id}/`);
  },
  duplicateRespiratoryStation(id) {
    return api.post(`mocks/respiratory/stations/${id}/duplicate/`);
  },

  // Respiratory Body Parts Hierarchy
  getRespiratoryBodyPartsHierarchy() {
    return api.get('mocks/respiratory/body-parts-hierarchy/hierarchy/');
  },

  getRespiratoryBodyParts() {
    return api.get('mocks/respiratory/body-parts-hierarchy/');
  },

  createRespiratoryBodyPart(data) {
    return api.post('mocks/respiratory/body-parts-hierarchy/', data);
  },

  updateRespiratoryBodyPart(id, data) {
    return api.put(`mocks/respiratory/body-parts-hierarchy/${id}/`, data);
  },

  deleteRespiratoryBodyPart(id) {
    return api.delete(`mocks/respiratory/body-parts-hierarchy/${id}/`);
  },

  populateDefaultRespiratoryBodyParts() {
    return api.post('mocks/respiratory/body-parts-hierarchy/populate_defaults/');
  },

  // Respiratory Questions endpoints
  getRespiratoryQuestions(stationId) {
    return api.get(`mocks/respiratory/stations/${stationId}/questions/`);
  },

  submitRespiratoryAnswers(stationId, data) {
    return api.post(`mocks/respiratory/stations/${stationId}/submit-answers/`, data);
  },

  getRespiratoryStations(params = {}) {
    return api.get('mocks/respiratory/stations/', { params });
  },





  // Respiratory Station Folder endpoints - Pediatrics
  getRespiratoryPediatricsFolders(params = {}) {
    return api.get('mocks/respiratory-pediatrics/folders/', { params });
  },
  getRespiratoryPediatricsFoldersBySpecialty(specialty) {
    return api.get(`mocks/respiratory-pediatrics/folders/`);
  },
  createRespiratoryPediatricsFolder(data) {
    return api.post('mocks/respiratory-pediatrics/folders/', data);
  },
  updateRespiratoryPediatricsFolder(id, data) {
    return api.patch(`mocks/respiratory-pediatrics/folders/${id}/`, data);
  },
  deleteRespiratoryPediatricsFolder(id) {
    return api.delete(`mocks/respiratory-pediatrics/folders/${id}/`);
  },

  // Respiratory Pediatrics Station endpoints
  getRespiratoryPediatricsStations(params = {}) {
    return api.get('mocks/respiratory-pediatrics/stations/', { params });
  },
  getRespiratoryPediatricsStation(id) {
    return api.get(`mocks/respiratory-pediatrics/stations/${id}/`);
  },
  createRespiratoryPediatricsStation(data) {
    return api.post('mocks/respiratory-pediatrics/stations/', data);
  },
  updateRespiratoryPediatricsStation(id, data) {
    return api.put(`mocks/respiratory-pediatrics/stations/${id}/`, data);
  },
  deleteRespiratoryPediatricsStation(id) {
    return api.delete(`mocks/respiratory-pediatrics/stations/${id}/`);
  },
  duplicateRespiratoryPediatricsStation(id) {
    return api.post(`mocks/respiratory-pediatrics/stations/${id}/duplicate/`);
  },

  // Respiratory Pediatrics Body Parts Hierarchy
  getRespiratoryPediatricsBodyPartsHierarchy() {
    return api.get('mocks/respiratory-pediatrics/body-parts-hierarchy/hierarchy/');
  },

  getRespiratoryPediatricsBodyParts() {
    return api.get('mocks/respiratory-pediatrics/body-parts-hierarchy/');
  },

  createRespiratoryPediatricsBodyPart(data) {
    return api.post('mocks/respiratory-pediatrics/body-parts-hierarchy/', data);
  },

  updateRespiratoryPediatricsBodyPart(id, data) {
    return api.put(`mocks/respiratory-pediatrics/body-parts-hierarchy/${id}/`, data);
  },

  deleteRespiratoryPediatricsBodyPart(id) {
    return api.delete(`mocks/respiratory-pediatrics/body-parts-hierarchy/${id}/`);
  },

  populateDefaultRespiratoryPediatricsBodyParts() {
    return api.post('mocks/respiratory-pediatrics/body-parts-hierarchy/populate_defaults/');
  },

  // Respiratory Pediatrics Questions endpoints
  getRespiratoryPediatricsQuestions(stationId) {
    return api.get(`mocks/respiratory-pediatrics/stations/${stationId}/questions/`);
  },

  submitRespiratoryPediatricsAnswers(stationId, data) {
    return api.post(`mocks/respiratory-pediatrics/stations/${stationId}/submit-answers/`, data);
  },

  getRespiratoryPediatricsStations(params = {}) {
    return api.get('mocks/respiratory-pediatrics/stations/', { params });
  },









  // Gastrointestinal Pediatrics Station Folder endpoints - Pediatrics
  getGastrointestinalPediatricsFolders(params = {}) {
    return api.get('mocks/gastrointestinal-pediatrics/folders/', { params });
  },
  getGastrointestinalPediatricsFoldersBySpecialty(specialty) {
    return api.get(`mocks/gastrointestinal-pediatrics/folders/`);
  },
  createGastrointestinalPediatricsFolder(data) {
    return api.post('mocks/gastrointestinal-pediatrics/folders/', data);
  },
  updateGastrointestinalPediatricsFolder(id, data) {
    return api.patch(`mocks/gastrointestinal-pediatrics/folders/${id}/`, data);
  },
  deleteGastrointestinalPediatricsFolder(id) {
    return api.delete(`mocks/gastrointestinal-pediatrics/folders/${id}/`);
  },

  // Gastrointestinal Pediatrics Station endpoints
  getGastrointestinalPediatricsStations(params = {}) {
    return api.get('mocks/gastrointestinal-pediatrics/stations/', { params });
  },
  getGastrointestinalPediatricsStation(id) {
    return api.get(`mocks/gastrointestinal-pediatrics/stations/${id}/`);
  },
  createGastrointestinalPediatricsStation(data) {
    return api.post('mocks/gastrointestinal-pediatrics/stations/', data);
  },
  updateGastrointestinalPediatricsStation(id, data) {
    return api.put(`mocks/gastrointestinal-pediatrics/stations/${id}/`, data);
  },
  deleteGastrointestinalPediatricsStation(id) {
    return api.delete(`mocks/gastrointestinal-pediatrics/stations/${id}/`);
  },
  duplicateGastrointestinalPediatricsStation(id) {
    return api.post(`mocks/gastrointestinal-pediatrics/stations/${id}/duplicate/`);
  },

  // Gastrointestinal Body Parts Hierarchy
  getGastrointestinalPediatricsBodyPartsHierarchy() {
    return api.get('mocks/gastrointestinal-pediatrics/body-parts-hierarchy/hierarchy/');
  },

  getGastrointestinalPediatricsBodyParts() {
    return api.get('mocks/gastrointestinal-pediatrics/body-parts-hierarchy/');
  },

  createGastrointestinalPediatricsBodyPart(data) {
    return api.post('mocks/gastrointestinal-pediatrics/body-parts-hierarchy/', data);
  },

  updateGastrointestinalPediatricsBodyPart(id, data) {
    return api.put(`mocks/gastrointestinal-pediatrics/body-parts-hierarchy/${id}/`, data);
  },

  deleteGastrointestinalPediatricsBodyPart(id) {
    return api.delete(`mocks/gastrointestinal-pediatrics/body-parts-hierarchy/${id}/`);
  },

  populateDefaultGastrointestinalPediatricsBodyParts() {
    return api.post('mocks/gastrointestinal-pediatrics/body-parts-hierarchy/populate_defaults/');
  },

  // Gastrointestinal Pediatrics Questions endpoints - pediatrics
  getGastrointestinalPediatricsQuestions(stationId) {
    return api.get(`mocks/gastrointestinal-pediatrics/stations/${stationId}/questions/`);
  },

  submitGastrointestinalPediatricsAnswers(stationId, data) {
    return api.post(`mocks/gastrointestinal-pediatrics/stations/${stationId}/submit-answers/`, data);
  },

  getGastrointestinalPediatricsStations(params = {}) {
    return api.get('mocks/gastrointestinal-pediatrics/stations/', { params });
  },











  // Gastrointestinal Station Folder endpoints
  getGastrointestinalFolders(params = {}) {
    return api.get('mocks/gastrointestinal/folders/', { params });
  },
  getGastrointestinalFoldersBySpecialty(specialty) {
    return api.get(`mocks/gastrointestinal/folders/`);
  },
  createGastrointestinalFolder(data) {
    return api.post('mocks/gastrointestinal/folders/', data);
  },
  updateGastrointestinalFolder(id, data) {
    return api.patch(`mocks/gastrointestinal/folders/${id}/`, data);
  },
  deleteGastrointestinalFolder(id) {
    return api.delete(`mocks/gastrointestinal/folders/${id}/`);
  },

  // Gastrointestinal Station endpoints
  getGastrointestinalStations(params = {}) {
    return api.get('mocks/gastrointestinal/stations/', { params });
  },
  getGastrointestinalStation(id) {
    return api.get(`mocks/gastrointestinal/stations/${id}/`);
  },
  createGastrointestinalStation(data) {
    return api.post('mocks/gastrointestinal/stations/', data);
  },
  updateGastrointestinalStation(id, data) {
    return api.put(`mocks/gastrointestinal/stations/${id}/`, data);
  },
  deleteGastrointestinalStation(id) {
    return api.delete(`mocks/gastrointestinal/stations/${id}/`);
  },
  duplicateGastrointestinalStation(id) {
    return api.post(`mocks/gastrointestinal/stations/${id}/duplicate/`);
  },

  // Gastrointestinal Body Parts Hierarchy
  getGastrointestinalBodyPartsHierarchy() {
    return api.get('mocks/gastrointestinal/body-parts-hierarchy/hierarchy/');
  },

  getGastrointestinalBodyParts() {
    return api.get('mocks/gastrointestinal/body-parts-hierarchy/');
  },

  createGastrointestinalBodyPart(data) {
    return api.post('mocks/gastrointestinal/body-parts-hierarchy/', data);
  },

  updateGastrointestinalBodyPart(id, data) {
    return api.put(`mocks/gastrointestinal/body-parts-hierarchy/${id}/`, data);
  },

  deleteGastrointestinalBodyPart(id) {
    return api.delete(`mocks/gastrointestinal/body-parts-hierarchy/${id}/`);
  },

  populateDefaultGastrointestinalBodyParts() {
    return api.post('mocks/gastrointestinal/body-parts-hierarchy/populate_defaults/');
  },

  // Gastrointestinal Questions endpoints
  getGastrointestinalQuestions(stationId) {
    return api.get(`mocks/gastrointestinal/stations/${stationId}/questions/`);
  },

  submitGastrointestinalAnswers(stationId, data) {
    return api.post(`mocks/gastrointestinal/stations/${stationId}/submit-answers/`, data);
  },

  getGastrointestinalStations(params = {}) {
    return api.get('mocks/gastrointestinal/stations/', { params });
  },






  // Neurological Station Folder endpoints
  getNeurologicalFolders(params = {}) {
    return api.get('mocks/neurological/folders/', { params });
  },
  getNeurologicalFoldersBySpecialty(specialty) {
    return api.get(`mocks/neurological/folders/`);
  },
  createNeurologicalFolder(data) {
    return api.post('mocks/neurological/folders/', data);
  },
  updateNeurologicalFolder(id, data) {
    return api.patch(`mocks/neurological/folders/${id}/`, data);
  },
  deleteNeurologicalFolder(id) {
    return api.delete(`mocks/neurological/folders/${id}/`);
  },

  // Neurological Station endpoints
  getNeurologicalStations(params = {}) {
    return api.get('mocks/neurological/stations/', { params });
  },
  getNeurologicalStation(id) {
    return api.get(`mocks/neurological/stations/${id}/`);
  },
  createNeurologicalStation(data) {
    return api.post('mocks/neurological/stations/', data);
  },
  updateNeurologicalStation(id, data) {
    return api.put(`mocks/neurological/stations/${id}/`, data);
  },
  deleteNeurologicalStation(id) {
    return api.delete(`mocks/neurological/stations/${id}/`);
  },
  duplicateNeurologicalStation(id) {
    return api.post(`mocks/neurological/stations/${id}/duplicate/`);
  },

  // Neurological Body Parts Hierarchy
  getNeurologicalBodyPartsHierarchy() {
    return api.get('mocks/neurological/body-parts-hierarchy/hierarchy/');
  },

  getNeurologicalBodyParts() {
    return api.get('mocks/neurological/body-parts-hierarchy/');
  },

  createNeurologicalBodyPart(data) {
    return api.post('mocks/neurological/body-parts-hierarchy/', data);
  },

  updateNeurologicalBodyPart(id, data) {
    return api.put(`mocks/neurological/body-parts-hierarchy/${id}/`, data);
  },

  deleteNeurologicalBodyPart(id) {
    return api.delete(`mocks/neurological/body-parts-hierarchy/${id}/`);
  },

  populateDefaultNeurologicalBodyParts() {
    return api.post('mocks/neurological/body-parts-hierarchy/populate_defaults/');
  },

  // Neurological Questions endpoints
  getNeurologicalQuestions(stationId) {
    return api.get(`mocks/neurological/stations/${stationId}/questions/`);
  },

  submitNeurologicalAnswers(stationId, data) {
    return api.post(`mocks/neurological/stations/${stationId}/submit-answers/`, data);
  },

  getNeurologicalStations(params = {}) {
    return api.get('mocks/neurological/stations/', { params });
  },













  // Neurological Station Folder endpoints - Pediatrics
  getNeurologicalPediatricsFolders(params = {}) {
    return api.get('mocks/neurological-pediatrics/folders/', { params });
  },
  getNeurologicalPediatricsFoldersBySpecialty(specialty) {
    return api.get(`mocks/neurological-pediatrics/folders/`);
  },
  createNeurologicalPediatricsFolder(data) {
    return api.post('mocks/neurological-pediatrics/folders/', data);
  },
  updateNeurologicalPediatricsFolder(id, data) {
    return api.patch(`mocks/neurological-pediatrics/folders/${id}/`, data);
  },
  deleteNeurologicalPediatricsFolder(id) {
    return api.delete(`mocks/neurological-pediatrics/folders/${id}/`);
  },

  // Neurological Station endpoints - Pediatrics
  getNeurologicalPediatricsStations(params = {}) {
    return api.get('mocks/neurological-pediatrics/stations/', { params });
  },
  getNeurologicalPediatricsStation(id) {
    return api.get(`mocks/neurological-pediatrics/stations/${id}/`);
  },
  createNeurologicalPediatricsStation(data) {
    return api.post('mocks/neurological-pediatrics/stations/', data);
  },
  updateNeurologicalPediatricsStation(id, data) {
    return api.put(`mocks/neurological-pediatrics/stations/${id}/`, data);
  },
  deleteNeurologicalPediatricsStation(id) {
    return api.delete(`mocks/neurological-pediatrics/stations/${id}/`);
  },
  duplicateNeurologicalPediatricsStation(id) {
    return api.post(`mocks/neurological-pediatrics/stations/${id}/duplicate/`);
  },

  // neurological Body Parts Hierarchy
  getNeurologicalPediatricsBodyPartsHierarchy() {
    return api.get('mocks/neurological-pediatrics/body-parts-hierarchy/hierarchy/');
  },

  getNeurologicalPediatricsBodyParts() {
    return api.get('mocks/neurological-pediatrics/body-parts-hierarchy/');
  },

  createNeurologicalPediatricsBodyPart(data) {
    return api.post('mocks/neurological-pediatrics/body-parts-hierarchy/', data);
  },

  updateNeurologicalPediatricsBodyPart(id, data) {
    return api.put(`mocks/neurological-pediatrics/body-parts-hierarchy/${id}/`, data);
  },

  deleteNeurologicalPediatricsBodyPart(id) {
    return api.delete(`mocks/neurological-pediatrics/body-parts-hierarchy/${id}/`);
  },

  populateDefaultNeurologicalPediatricsBodyParts() {
    return api.post('mocks/neurological-pediatrics/body-parts-hierarchy/populate_defaults/');
  },

  // Neurological Questions endpoints - pediatrics
  getNeurologicalPediatricsQuestions(stationId) {
    return api.get(`mocks/neurological-pediatrics/stations/${stationId}/questions/`);
  },

  submitNeurologicalPediatricsAnswers(stationId, data) {
    return api.post(`mocks/neurological-pediatrics/stations/${stationId}/submit-answers/`, data);
  },

  getNeurologicalPediatricsStations(params = {}) {
    return api.get('mocks/neurological-pediatrics/stations/', { params });
  },









  // General Station Folder endpoints - Pediatrics
  getGeneralPediatricsFolders(params = {}) {
    return api.get('mocks/general-pediatrics/folders/', { params });
  },
  getGeneralPediatricsFoldersBySpecialty(specialty) {
    return api.get(`mocks/general-pediatrics/folders/`);
  },
  createGeneralPediatricsFolder(data) {
    return api.post('mocks/general-pediatrics/folders/', data);
  },
  updateGeneralPediatricsFolder(id, data) {
    return api.patch(`mocks/general-pediatrics/folders/${id}/`, data);
  },
  deleteGeneralPediatricsFolder(id) {
    return api.delete(`mocks/general-pediatrics/folders/${id}/`);
  },

  // General Station endpoints
  getGeneralPediatricsStations(params = {}) {
    return api.get('mocks/general-pediatrics/stations/', { params });
  },
  getGeneralPediatricsStation(id) {
    return api.get(`mocks/general-pediatrics/stations/${id}/`);
  },
  createGeneralPediatricsStation(data) {
    return api.post('mocks/general-pediatrics/stations/', data);
  },
  updateGeneralPediatricsStation(id, data) {
    return api.put(`mocks/general-pediatrics/stations/${id}/`, data);
  },
  deleteGeneralPediatricsStation(id) {
    return api.delete(`mocks/general-pediatrics/stations/${id}/`);
  },
  duplicateGeneralPediatricsStation(id) {
    return api.post(`mocks/general-pediatrics/stations/${id}/duplicate/`);
  },

  // General Body Parts Hierarchy
  getGeneralPediatricsBodyPartsHierarchy() {
    return api.get('mocks/general-pediatrics/body-parts-hierarchy/hierarchy/');
  },

  getGeneralPediatricsBodyParts() {
    return api.get('mocks/general-pediatrics/body-parts-hierarchy/');
  },

  createGeneralPediatricsBodyPart(data) {
    return api.post('mocks/general-pediatrics/body-parts-hierarchy/', data);
  },

  updateGeneralPediatricsBodyPart(id, data) {
    return api.put(`mocks/general-pediatrics/body-parts-hierarchy/${id}/`, data);
  },

  deleteGeneralPediatricsBodyPart(id) {
    return api.delete(`mocks/general-pediatrics/body-parts-hierarchy/${id}/`);
  },

  populateDefaultGeneralPediatricsBodyParts() {
    return api.post('mocks/general-pediatrics/body-parts-hierarchy/populate_defaults/');
  },

  // General Questions endpoints - pediatrics
  getGeneralPediatricsQuestions(stationId) {
    return api.get(`mocks/general-pediatrics/stations/${stationId}/questions/`);
  },

  submitGeneralPediatricsAnswers(stationId, data) {
    return api.post(`mocks/general-pediatrics/stations/${stationId}/submit-answers/`, data);
  },

  getGeneralPediatricsStations(params = {}) {
    return api.get('mocks/general-pediatrics/stations/', { params });
  },








  // General Station Folder endpoints 
  getGeneralFolders(params = {}) {
    return api.get('mocks/general/folders/', { params });
  },
  getGeneralFoldersBySpecialty(specialty) {
    return api.get(`mocks/general/folders/`);
  },
  createGeneralFolder(data) {
    return api.post('mocks/general/folders/', data);
  },
  updateGeneralFolder(id, data) {
    return api.patch(`mocks/general/folders/${id}/`, data);
  },
  deleteGeneralFolder(id) {
    return api.delete(`mocks/general/folders/${id}/`);
  },

  // General Station endpoints
  getGeneralStations(params = {}) {
    return api.get('mocks/general/stations/', { params });
  },
  getGeneralStation(id) {
    return api.get(`mocks/general/stations/${id}/`);
  },
  createGeneralStation(data) {
    return api.post('mocks/general/stations/', data);
  },
  updateGeneralStation(id, data) {
    return api.put(`mocks/general/stations/${id}/`, data);
  },
  deleteGeneralStation(id) {
    return api.delete(`mocks/general/stations/${id}/`);
  },
  duplicateGeneralStation(id) {
    return api.post(`mocks/general/stations/${id}/duplicate/`);
  },

  // General Body Parts Hierarchy
  getGeneralBodyPartsHierarchy() {
    return api.get('mocks/general/body-parts-hierarchy/hierarchy/');
  },

  getGeneralBodyParts() {
    return api.get('mocks/general/body-parts-hierarchy/');
  },

  createGeneralBodyPart(data) {
    return api.post('mocks/general/body-parts-hierarchy/', data);
  },

  updateGeneralBodyPart(id, data) {
    return api.put(`mocks/general/body-parts-hierarchy/${id}/`, data);
  },

  deleteGeneralBodyPart(id) {
    return api.delete(`mocks/general/body-parts-hierarchy/${id}/`);
  },

  populateDefaultGeneralBodyParts() {
    return api.post('mocks/general/body-parts-hierarchy/populate_defaults/');
  },

  // General Questions endpoints
  getGeneralQuestions(stationId) {
    return api.get(`mocks/general/stations/${stationId}/questions/`);
  },

  submitGeneralAnswers(stationId, data) {
    return api.post(`mocks/general/stations/${stationId}/submit-answers/`, data);
  },

  getGeneralStations(params = {}) {
    return api.get('mocks/general/stations/', { params });
  },





































  // Respiratory Station Folder endpoints
  getRespiratoryFolders(params = {}) {
    return api.get('mocks/respiratory/folders/', { params });
  },
  getRespiratoryFoldersBySpecialty(specialty) {
    return api.get(`mocks/respiratory/folders/`);
  },
  createRespiratoryFolder(data) {
    return api.post('mocks/respiratory/folders/', data);
  },
  updateRespiratoryFolder(id, data) {
    return api.patch(`mocks/respiratory/folders/${id}/`, data);
  },
  deleteRespiratoryFolder(id) {
    return api.delete(`mocks/respiratory/folders/${id}/`);
  },

  // Respiratory Station endpoints
  getRespiratoryStations(params = {}) {
    return api.get('mocks/respiratory/stations/', { params });
  },
  getRespiratoryStation(id) {
    return api.get(`mocks/respiratory/stations/${id}/`);
  },
  createRespiratoryStation(data) {
    return api.post('mocks/respiratory/stations/', data);
  },
  updateRespiratoryStation(id, data) {
    return api.put(`mocks/respiratory/stations/${id}/`, data);
  },
  deleteRespiratoryStation(id) {
    return api.delete(`mocks/respiratory/stations/${id}/`);
  },
  duplicateRespiratoryStation(id) {
    return api.post(`mocks/respiratory/stations/${id}/duplicate/`);
  },

  // Gastrointestinal Station Folder endpoints
  getGastrointestinalFolders(params = {}) {
    return api.get('mocks/gastrointestinal/folders/', { params });
  },
  getGastrointestinalFoldersBySpecialty(specialty) {
    return api.get(`mocks/gastrointestinal/folders/`);
  },
  createGastrointestinalFolder(data) {
    return api.post('mocks/gastrointestinal/folders/', data);
  },
  updateGastrointestinalFolder(id, data) {
    return api.patch(`mocks/gastrointestinal/folders/${id}/`, data);
  },
  deleteGastrointestinalFolder(id) {
    return api.delete(`mocks/gastrointestinal/folders/${id}/`);
  },

  // Gastrointestinal Station endpoints
  getGastrointestinalStations(params = {}) {
    return api.get('mocks/gastrointestinal/stations/', { params });
  },
  getGastrointestinalStation(id) {
    return api.get(`mocks/gastrointestinal/stations/${id}/`);
  },
  createGastrointestinalStation(data) {
    return api.post('mocks/gastrointestinal/stations/', data);
  },
  updateGastrointestinalStation(id, data) {
    return api.put(`mocks/gastrointestinal/stations/${id}/`, data);
  },
  deleteGastrointestinalStation(id) {
    return api.delete(`mocks/gastrointestinal/stations/${id}/`);
  },
  duplicateGastrointestinalStation(id) {
    return api.post(`mocks/gastrointestinal/stations/${id}/duplicate/`);
  },

  // Neurological Station Folder endpoints
  getNeurologicalFolders(params = {}) {
    return api.get('mocks/neurological/folders/', { params });
  },
  getNeurologicalFoldersBySpecialty(specialty) {
    return api.get(`mocks/neurological/folders/`);
  },
  createNeurologicalFolder(data) {
    return api.post('mocks/neurological/folders/', data);
  },
  updateNeurologicalFolder(id, data) {
    return api.patch(`mocks/neurological/folders/${id}/`, data);
  },
  deleteNeurologicalFolder(id) {
    return api.delete(`mocks/neurological/folders/${id}/`);
  },

  // Neurological Station endpoints
  getNeurologicalStations(params = {}) {
    return api.get('mocks/neurological/stations/', { params });
  },
  getNeurologicalStation(id) {
    return api.get(`mocks/neurological/stations/${id}/`);
  },
  createNeurologicalStation(data) {
    return api.post('mocks/neurological/stations/', data);
  },
  updateNeurologicalStation(id, data) {
    return api.put(`mocks/neurological/stations/${id}/`, data);
  },
  deleteNeurologicalStation(id) {
    return api.delete(`mocks/neurological/stations/${id}/`);
  },
  duplicateNeurologicalStation(id) {
    return api.post(`mocks/neurological/stations/${id}/duplicate/`);
  },





  // Breast Station Folder endpoints
  getBreastFolders(params = {}) {
    return api.get('mocks/breast/folders/', { params });
  },
  getBreastFoldersBySpecialty(specialty) {
    return api.get(`mocks/breast/folders/`);
  },
  createBreastFolder(data) {
    return api.post('mocks/breast/folders/', data);
  },
  updateBreastFolder(id, data) {
    return api.patch(`mocks/breast/folders/${id}/`, data);
  },
  deleteBreastFolder(id) {
    return api.delete(`mocks/breast/folders/${id}/`);
  },

  // Breast Station endpoints
  getBreastStations(params = {}) {
    return api.get('mocks/breast/stations/', { params });
  },
  getBreastStation(id) {
    return api.get(`mocks/breast/stations/${id}/`);
  },
  createBreastStation(data) {
    return api.post('mocks/breast/stations/', data);
  },
  updateBreastStation(id, data) {
    return api.put(`mocks/breast/stations/${id}/`, data);
  },
  deleteBreastStation(id) {
    return api.delete(`mocks/breast/stations/${id}/`);
  },
  duplicateBreastStation(id) {
    return api.post(`mocks/breast/stations/${id}/duplicate/`);
  },

  // Breast Body Parts Hierarchy
  getBreastBodyPartsHierarchy() {
    return api.get('mocks/breast/body-parts-hierarchy/hierarchy/');
  },

  getBreastBodyParts() {
    return api.get('mocks/breast/body-parts-hierarchy/');
  },

  createBreastBodyPart(data) {
    return api.post('mocks/breast/body-parts-hierarchy/', data);
  },

  updateBreastBodyPart(id, data) {
    return api.put(`mocks/breast/body-parts-hierarchy/${id}/`, data);
  },

  deleteBreastBodyPart(id) {
    return api.delete(`mocks/breast/body-parts-hierarchy/${id}/`);
  },

  populateDefaultBreastBodyParts() {
    return api.post('mocks/breast/body-parts-hierarchy/populate_defaults/');
  },

  // Breast Questions endpoints - pediatrics
  getBreastQuestions(stationId) {
    return api.get(`mocks/breast/stations/${stationId}/questions/`);
  },

  submitBreastAnswers(stationId, data) {
    return api.post(`mocks/breast/stations/${stationId}/submit-answers/`, data);
  },

  getBreastStations(params = {}) {
    return api.get('mocks/breast/stations/', { params });
  },






  // Ulcer Station Folder endpoints 
  getUlcerFolders(params = {}) {
    return api.get('mocks/ulcer/folders/', { params });
  },
  getUlcerFoldersBySpecialty(specialty) {
    return api.get(`mocks/ulcer/folders/`);
  },
  createUlcerFolder(data) {
    return api.post('mocks/ulcer/folders/', data);
  },
  updateUlcerFolder(id, data) {
    return api.patch(`mocks/ulcer/folders/${id}/`, data);
  },
  deleteUlcerFolder(id) {
    return api.delete(`mocks/ulcer/folders/${id}/`);
  },

  // Ulcer Station endpoints
  getUlcerStations(params = {}) {
    return api.get('mocks/ulcer/stations/', { params });
  },
  getUlcerStation(id) {
    return api.get(`mocks/ulcer/stations/${id}/`);
  },
  createUlcerStation(data) {
    return api.post('mocks/ulcer/stations/', data);
  },
  updateUlcerStation(id, data) {
    return api.put(`mocks/ulcer/stations/${id}/`, data);
  },
  deleteUlcerStation(id) {
    return api.delete(`mocks/ulcer/stations/${id}/`);
  },
  duplicateUlcerStation(id) {
    return api.post(`mocks/ulcer/stations/${id}/duplicate/`);
  },

  // Ulcer Body Parts Hierarchy
  getUlcerBodyPartsHierarchy() {
    return api.get('mocks/ulcer/body-parts-hierarchy/hierarchy/');
  },

  getUlcerBodyParts() {
    return api.get('mocks/ulcer/body-parts-hierarchy/');
  },

  createUlcerBodyPart(data) {
    return api.post('mocks/ulcer/body-parts-hierarchy/', data);
  },

  updateUlcerBodyPart(id, data) {
    return api.put(`mocks/ulcer/body-parts-hierarchy/${id}/`, data);
  },

  deleteUlcerBodyPart(id) {
    return api.delete(`mocks/ulcer/body-parts-hierarchy/${id}/`);
  },

  populateDefaultUlcerBodyParts() {
    return api.post('mocks/ulcer/body-parts-hierarchy/populate_defaults/');
  },

  // Ulcer Questions endpoints - pediatrics
  getUlcerQuestions(stationId) {
    return api.get(`mocks/ulcer/stations/${stationId}/questions/`);
  },

  submitUlcerAnswers(stationId, data) {
    return api.post(`mocks/ulcer/stations/${stationId}/submit-answers/`, data);
  },

  getUlcerStations(params = {}) {
    return api.get('mocks/ulcer/stations/', { params });
  },








  // Thyroid Station Folder endpoints - Pediatrics
  getThyroidFolders(params = {}) {
    return api.get('mocks/thyroid/folders/', { params });
  },
  getThyroidFoldersBySpecialty(specialty) {
    return api.get(`mocks/thyroid/folders/`);
  },
  createThyroidFolder(data) {
    return api.post('mocks/thyroid/folders/', data);
  },
  updateThyroidFolder(id, data) {
    return api.patch(`mocks/thyroid/folders/${id}/`, data);
  },
  deleteThyroidFolder(id) {
    return api.delete(`mocks/thyroid/folders/${id}/`);
  },

  // Thyroid Station endpoints
  getThyroidStations(params = {}) {
    return api.get('mocks/thyroid/stations/', { params });
  },
  getThyroidStation(id) {
    return api.get(`mocks/thyroid/stations/${id}/`);
  },
  createThyroidStation(data) {
    return api.post('mocks/thyroid/stations/', data);
  },
  updateThyroidStation(id, data) {
    return api.put(`mocks/thyroid/stations/${id}/`, data);
  },
  deleteThyroidStation(id) {
    return api.delete(`mocks/thyroid/stations/${id}/`);
  },
  duplicateThyroidStation(id) {
    return api.post(`mocks/thyroid/stations/${id}/duplicate/`);
  },

  // Thyroid Body Parts Hierarchy
  getThyroidBodyPartsHierarchy() {
    return api.get('mocks/thyroid/body-parts-hierarchy/hierarchy/');
  },

  getThyroidBodyParts() {
    return api.get('mocks/thyroid/body-parts-hierarchy/');
  },

  createThyroidBodyPart(data) {
    return api.post('mocks/thyroid/body-parts-hierarchy/', data);
  },

  updateThyroidBodyPart(id, data) {
    return api.put(`mocks/thyroid/body-parts-hierarchy/${id}/`, data);
  },

  deleteThyroidBodyPart(id) {
    return api.delete(`mocks/thyroid/body-parts-hierarchy/${id}/`);
  },

  populateDefaultThyroidBodyParts() {
    return api.post('mocks/thyroid/body-parts-hierarchy/populate_defaults/');
  },

  // Thyroid Questions endpoints - pediatrics
  getThyroidQuestions(stationId) {
    return api.get(`mocks/thyroid/stations/${stationId}/questions/`);
  },

  submitThyroidAnswers(stationId, data) {
    return api.post(`mocks/thyroid/stations/${stationId}/submit-answers/`, data);
  },

  getThyroidStations(params = {}) {
    return api.get('mocks/thyroid/stations/', { params });
  },






  // Hernia Station Folder endpoints 
  getHerniaFolders(params = {}) {
    return api.get('mocks/hernia/folders/', { params });
  },
  getHerniaFoldersBySpecialty(specialty) {
    return api.get(`mocks/hernia/folders/`);
  },
  createHerniaFolder(data) {
    return api.post('mocks/hernia/folders/', data);
  },
  updateHerniaFolder(id, data) {
    return api.patch(`mocks/hernia/folders/${id}/`, data);
  },
  deleteHerniaFolder(id) {
    return api.delete(`mocks/hernia/folders/${id}/`);
  },

  // Hernia Station endpoints
  getHerniaStations(params = {}) {
    return api.get('mocks/hernia/stations/', { params });
  },
  getHerniaStation(id) {
    return api.get(`mocks/hernia/stations/${id}/`);
  },
  createHerniaStation(data) {
    return api.post('mocks/hernia/stations/', data);
  },
  updateHerniaStation(id, data) {
    return api.put(`mocks/hernia/stations/${id}/`, data);
  },
  deleteHerniaStation(id) {
    return api.delete(`mocks/hernia/stations/${id}/`);
  },
  duplicateHerniaStation(id) {
    return api.post(`mocks/hernia/stations/${id}/duplicate/`);
  },

  // Hernia Body Parts Hierarchy
  getHerniaBodyPartsHierarchy() {
    return api.get('mocks/hernia/body-parts-hierarchy/hierarchy/');
  },

  getHerniaBodyParts() {
    return api.get('mocks/hernia/body-parts-hierarchy/');
  },

  createHerniaBodyPart(data) {
    return api.post('mocks/hernia/body-parts-hierarchy/', data);
  },

  updateHerniaBodyPart(id, data) {
    return api.put(`mocks/hernia/body-parts-hierarchy/${id}/`, data);
  },

  deleteHerniaBodyPart(id) {
    return api.delete(`mocks/hernia/body-parts-hierarchy/${id}/`);
  },

  populateDefaultHerniaBodyParts() {
    return api.post('mocks/hernia/body-parts-hierarchy/populate_defaults/');
  },

  // Hernia Questions endpoints - pediatrics
  getHerniaQuestions(stationId) {
    return api.get(`mocks/hernia/stations/${stationId}/questions/`);
  },

  submitHerniaAnswers(stationId, data) {
    return api.post(`mocks/hernia/stations/${stationId}/submit-answers/`, data);
  },

  getHerniaStations(params = {}) {
    return api.get('mocks/hernia/stations/', { params });
  },














  // NeuroSurgery Station Folder endpoints - Pediatrics
  getNeuroSurgeryFolders(params = {}) {
    return api.get('mocks/neuro-surgery/folders/', { params });
  },
  getNeuroSurgeryFoldersBySpecialty(specialty) {
    return api.get(`mocks/neuro-surgery/folders/`);
  },
  createNeuroSurgeryFolder(data) {
    return api.post('mocks/neuro-surgery/folders/', data);
  },
  updateNeuroSurgeryFolder(id, data) {
    return api.patch(`mocks/neuro-surgery/folders/${id}/`, data);
  },
  deleteNeuroSurgeryFolder(id) {
    return api.delete(`mocks/neuro-surgery/folders/${id}/`);
  },

  // NeuroSurgery Station endpoints
  getNeuroSurgeryStations(params = {}) {
    return api.get('mocks/neuro-surgery/stations/', { params });
  },
  getNeuroSurgeryStation(id) {
    return api.get(`mocks/neuro-surgery/stations/${id}/`);
  },
  createNeuroSurgeryStation(data) {
    return api.post('mocks/neuro-surgery/stations/', data);
  },
  updateNeuroSurgeryStation(id, data) {
    return api.put(`mocks/neuro-surgery/stations/${id}/`, data);
  },
  deleteNeuroSurgeryStation(id) {
    return api.delete(`mocks/neuro-surgery/stations/${id}/`);
  },
  duplicateNeuroSurgeryStation(id) {
    return api.post(`mocks/neuro-surgery/stations/${id}/duplicate/`);
  },

  // Neuro Surgery Body Parts Hierarchy
  getNeuroSurgeryBodyPartsHierarchy() {
    return api.get('mocks/neuro-surgery/body-parts-hierarchy/hierarchy/');
  },

  getNeuroSurgeryBodyParts() {
    return api.get('mocks/neuro-surgery/body-parts-hierarchy/');
  },

  createNeuroSurgeryBodyPart(data) {
    return api.post('mocks/neuro-surgery/body-parts-hierarchy/', data);
  },

  updateNeuroSurgeryBodyPart(id, data) {
    return api.put(`mocks/neuro-surgery/body-parts-hierarchy/${id}/`, data);
  },

  deleteNeuroSurgeryBodyPart(id) {
    return api.delete(`mocks/neuro-surgery/body-parts-hierarchy/${id}/`);
  },

  populateDefaultNeuroSurgeryBodyParts() {
    return api.post('mocks/neuro-surgery/body-parts-hierarchy/populate_defaults/');
  },

  // NeuroSurgery Questions endpoints - pediatrics
  getNeuroSurgeryQuestions(stationId) {
    return api.get(`mocks/neuro-surgery/stations/${stationId}/questions/`);
  },

  submitNeuroSurgeryAnswers(stationId, data) {
    return api.post(`mocks/neuro-surgery/stations/${stationId}/submit-answers/`, data);
  },

  getNeuroSurgeryStations(params = {}) {
    return api.get('mocks/neuro-surgery/stations/', { params });
  },










  // Cardiothoracic Station Folder endpoints - Pediatrics
  getCardiothoracicFolders(params = {}) {
    return api.get('mocks/cardiothoracic/folders/', { params });
  },
  getCardiothoracicFoldersBySpecialty(specialty) {
    return api.get(`mocks/cardiothoracic/folders/`);
  },
  createCardiothoracicFolder(data) {
    return api.post('mocks/cardiothoracic/folders/', data);
  },
  updateCardiothoracicFolder(id, data) {
    return api.patch(`mocks/cardiothoracic/folders/${id}/`, data);
  },
  deleteCardiothoracicFolder(id) {
    return api.delete(`mocks/cardiothoracic/folders/${id}/`);
  },

  // Cardiothoracic Station endpoints
  getCardiothoracicStations(params = {}) {
    return api.get('mocks/cardiothoracic/stations/', { params });
  },
  getCardiothoracicStation(id) {
    return api.get(`mocks/cardiothoracic/stations/${id}/`);
  },
  createCardiothoracicStation(data) {
    return api.post('mocks/cardiothoracic/stations/', data);
  },
  updateCardiothoracicStation(id, data) {
    return api.put(`mocks/cardiothoracic/stations/${id}/`, data);
  },
  deleteCardiothoracicStation(id) {
    return api.delete(`mocks/cardiothoracic/stations/${id}/`);
  },
  duplicateCardiothoracicStation(id) {
    return api.post(`mocks/cardiothoracic/stations/${id}/duplicate/`);
  },

  // Cardiothoracic Body Parts Hierarchy
  getCardiothoracicBodyPartsHierarchy() {
    return api.get('mocks/cardiothoracic/body-parts-hierarchy/hierarchy/');
  },

  getCardiothoracicBodyParts() {
    return api.get('mocks/cardiothoracic/body-parts-hierarchy/');
  },

  createCardiothoracicBodyPart(data) {
    return api.post('mocks/cardiothoracic/body-parts-hierarchy/', data);
  },

  updateCardiothoracicBodyPart(id, data) {
    return api.put(`mocks/cardiothoracic/body-parts-hierarchy/${id}/`, data);
  },

  deleteCardiothoracicBodyPart(id) {
    return api.delete(`mocks/cardiothoracic/body-parts-hierarchy/${id}/`);
  },

  populateDefaultCardiothoracicBodyParts() {
    return api.post('mocks/cardiothoracic/body-parts-hierarchy/populate_defaults/');
  },

  // Cardiothoracic Questions endpoints - pediatrics
  getCardiothoracicQuestions(stationId) {
    return api.get(`mocks/cardiothoracic/stations/${stationId}/questions/`);
  },

  submitCardiothoracicAnswers(stationId, data) {
    return api.post(`mocks/cardiothoracic/stations/${stationId}/submit-answers/`, data);
  },

  getCardiothoracicStations(params = {}) {
    return api.get('mocks/cardiothoracic/stations/', { params });
  },










  // Plastic Surgery Station Folder endpoints - Pediatrics
  getPlasticSurgeryFolders(params = {}) {
    return api.get('mocks/plastic-surgery/folders/', { params });
  },
  getPlasticSurgeryFoldersBySpecialty(specialty) {
    return api.get(`mocks/plastic-surgery/folders/`);
  },
  createPlasticSurgeryFolder(data) {
    return api.post('mocks/plastic-surgery/folders/', data);
  },
  updatePlasticSurgeryFolder(id, data) {
    return api.patch(`mocks/plastic-surgery/folders/${id}/`, data);
  },
  deletePlasticSurgeryFolder(id) {
    return api.delete(`mocks/plastic-surgery/folders/${id}/`);
  },

  // Plastic Surgery Station endpoints
  getPlasticSurgeryStations(params = {}) {
    return api.get('mocks/plastic-surgery/stations/', { params });
  },
  getPlasticSurgeryStation(id) {
    return api.get(`mocks/plastic-surgery/stations/${id}/`);
  },
  createPlasticSurgeryStation(data) {
    return api.post('mocks/plastic-surgery/stations/', data);
  },
  updatePlasticSurgeryStation(id, data) {
    return api.put(`mocks/plastic-surgery/stations/${id}/`, data);
  },
  deletePlasticSurgeryStation(id) {
    return api.delete(`mocks/plastic-surgery/stations/${id}/`);
  },
  duplicatePlasticSurgeryStation(id) {
    return api.post(`mocks/plastic-surgery/stations/${id}/duplicate/`);
  },

  // Plastic Surgery Body Parts Hierarchy
  getPlasticSurgeryBodyPartsHierarchy() {
    return api.get('mocks/plastic-surgery/body-parts-hierarchy/hierarchy/');
  },

  getPlasticSurgeryBodyParts() {
    return api.get('mocks/plastic-surgery/body-parts-hierarchy/');
  },

  createPlasticSurgeryBodyPart(data) {
    return api.post('mocks/plastic-surgery/body-parts-hierarchy/', data);
  },

  updatePlasticSurgeryBodyPart(id, data) {
    return api.put(`mocks/plastic-surgery/body-parts-hierarchy/${id}/`, data);
  },

  deletePlasticSurgeryBodyPart(id) {
    return api.delete(`mocks/plastic-surgery/body-parts-hierarchy/${id}/`);
  },

  populateDefaultPlasticSurgeryBodyParts() {
    return api.post('mocks/plastic-surgery/body-parts-hierarchy/populate_defaults/');
  },

  // Plastic Surgery Questions endpoints - pediatrics
  getPlasticSurgeryQuestions(stationId) {
    return api.get(`mocks/plastic-surgery/stations/${stationId}/questions/`);
  },

  submitPlasticSurgeryAnswers(stationId, data) {
    return api.post(`mocks/plastic-surgery/stations/${stationId}/submit-answers/`, data);
  },

  getPlasticSurgeryStations(params = {}) {
    return api.get('mocks/plastic-surgery/stations/', { params });
  },













    // Vascular Surgery Station Folder endpoints - Pediatrics
  getVascularSurgeryFolders(params = {}) {
    return api.get('mocks/vascular-surgery/folders/', { params });
  },
  getVascularSurgeryFoldersBySpecialty(specialty) {
    return api.get(`mocks/vascular-surgery/folders/`);
  },
  createVascularSurgeryFolder(data) {
    return api.post('mocks/vascular-surgery/folders/', data);
  },
  updateVascularSurgeryFolder(id, data) {
    return api.patch(`mocks/vascular-surgery/folders/${id}/`, data);
  },
  deleteVascularSurgeryFolder(id) {
    return api.delete(`mocks/vascular-surgery/folders/${id}/`);
  },

  // Vascular Station endpoints
  getVascularSurgeryStations(params = {}) {
    return api.get('mocks/vascular-surgery/stations/', { params });
  },
  getVascularSurgeryStation(id) {
    return api.get(`mocks/vascular-surgery/stations/${id}/`);
  },
  createVascularSurgeryStation(data) {
    return api.post('mocks/vascular-surgery/stations/', data);
  },
  updateVascularSurgeryStation(id, data) {
    return api.put(`mocks/vascular-surgery/stations/${id}/`, data);
  },
  deleteVascularSurgeryStation(id) {
    return api.delete(`mocks/vascular-surgery/stations/${id}/`);
  },
  duplicateVascularSurgeryStation(id) {
    return api.post(`mocks/vascular-surgery/stations/${id}/duplicate/`);
  },

  // Vascular Surgery Body Parts Hierarchy
  getVascularSurgeryBodyPartsHierarchy() {
    return api.get('mocks/vascular-surgery/body-parts-hierarchy/hierarchy/');
  },

  getVascularSurgeryBodyParts() {
    return api.get('mocks/vascular-surgery/body-parts-hierarchy/');
  },

  createVascularSurgeryBodyPart(data) {
    return api.post('mocks/vascular-surgery/body-parts-hierarchy/', data);
  },

  updateVascularSurgeryBodyPart(id, data) {
    return api.put(`mocks/vascular-surgery/body-parts-hierarchy/${id}/`, data);
  },

  deleteVascularSurgeryBodyPart(id) {
    return api.delete(`mocks/vascular-surgery/body-parts-hierarchy/${id}/`);
  },

  populateDefaultVascularSurgeryBodyParts() {
    return api.post('mocks/vascular-surgery/body-parts-hierarchy/populate_defaults/');
  },

  // Vascular Surgery Questions endpoints - pediatrics
  getVascularSurgeryQuestions(stationId) {
    return api.get(`mocks/vascular-surgery/stations/${stationId}/questions/`);
  },

  submitVascularSurgeryAnswers(stationId, data) {
    return api.post(`mocks/vascular-surgery/stations/${stationId}/submit-answers/`, data);
  },

  getVascularSurgeryStations(params = {}) {
    return api.get('mocks/vascular-surgery/stations/', { params });
  },













  // Pediatric Surgery Station Folder endpoints - Pediatrics
  getPediatricSurgeryFolders(params = {}) {
    return api.get('mocks/pediatric-surgery/folders/', { params });
  },
  getPediatricSurgeryFoldersBySpecialty(specialty) {
    return api.get(`mocks/pediatric-surgery/folders/`);
  },
  createPediatricSurgeryFolder(data) {
    return api.post('mocks/pediatric-surgery/folders/', data);
  },
  updatePediatricSurgeryFolder(id, data) {
    return api.patch(`mocks/pediatric-surgery/folders/${id}/`, data);
  },
  deletePediatricSurgeryFolder(id) {
    return api.delete(`mocks/pediatric-surgery/folders/${id}/`);
  },

  // Pediatric Surgery Station endpoints
  getPediatricSurgeryStations(params = {}) {
    return api.get('mocks/pediatric-surgery/stations/', { params });
  },
  getPediatricSurgeryStation(id) {
    return api.get(`mocks/pediatric-surgery/stations/${id}/`);
  },
  createPediatricSurgeryStation(data) {
    return api.post('mocks/pediatric-surgery/stations/', data);
  },
  updatePediatricSurgeryStation(id, data) {
    return api.put(`mocks/pediatric-surgery/stations/${id}/`, data);
  },
  deletePediatricSurgeryStation(id) {
    return api.delete(`mocks/pediatric-surgery/stations/${id}/`);
  },
  duplicatePediatricSurgeryStation(id) {
    return api.post(`mocks/pediatric-surgery/stations/${id}/duplicate/`);
  },

  // Pediatric Surgery Body Parts Hierarchy
  getPediatricSurgeryBodyPartsHierarchy() {
    return api.get('mocks/pediatric-surgery/body-parts-hierarchy/hierarchy/');
  },

  getPediatricSurgeryBodyParts() {
    return api.get('mocks/pediatric-surgery/body-parts-hierarchy/');
  },

  createPediatricSurgeryBodyPart(data) {
    return api.post('mocks/pediatric-surgery/body-parts-hierarchy/', data);
  },

  updatePediatricSurgeryBodyPart(id, data) {
    return api.put(`mocks/pediatric-surgery/body-parts-hierarchy/${id}/`, data);
  },

  deletePediatricSurgeryBodyPart(id) {
    return api.delete(`mocks/pediatric-surgery/body-parts-hierarchy/${id}/`);
  },

  populateDefaultPediatricSurgeryBodyParts() {
    return api.post('mocks/pediatric-surgery/body-parts-hierarchy/populate_defaults/');
  },

  // Pediatric Surgery Questions endpoints - pediatrics
  getPediatricSurgeryQuestions(stationId) {
    return api.get(`mocks/pediatric-surgery/stations/${stationId}/questions/`);
  },

  submitPediatricSurgeryAnswers(stationId, data) {
    return api.post(`mocks/pediatric-surgery/stations/${stationId}/submit-answers/`, data);
  },

  getPediatricSurgeryStations(params = {}) {
    return api.get('mocks/pediatric-surgery/stations/', { params });
  },







  // Pelvis Obgyn Station Folder endpoints 
  getPelvisObgynFolders(params = {}) {
    return api.get('mocks/pelvis-obgyn/folders/', { params });
  },
  getPelvisObgynFoldersBySpecialty(specialty) {
    return api.get(`mocks/pelvis-obgyn/folders/`);
  },
  createPelvisObgynFolder(data) {
    return api.post('mocks/pelvis-obgyn/folders/', data);
  },
  updatePelvisObgynFolder(id, data) {
    return api.patch(`mocks/pelvis-obgyn/folders/${id}/`, data);
  },
  deletePelvisObgynFolder(id) {
    return api.delete(`mocks/pelvis-obgyn/folders/${id}/`);
  },

  // Pelvis Obgyn Station endpoints
  getPelvisObgynStations(params = {}) {
    return api.get('mocks/pelvis-obgyn/stations/', { params });
  },
  getPelvisObgynStation(id) {
    return api.get(`mocks/pelvis-obgyn/stations/${id}/`);
  },
  createPelvisObgynStation(data) {
    return api.post('mocks/pelvis-obgyn/stations/', data);
  },
  updatePelvisObgynStation(id, data) {
    return api.put(`mocks/pelvis-obgyn/stations/${id}/`, data);
  },
  deletePelvisObgynStation(id) {
    return api.delete(`mocks/pelvis-obgyn/stations/${id}/`);
  },
  duplicatePelvisObgynStation(id) {
    return api.post(`mocks/pelvis-obgyn/stations/${id}/duplicate/`);
  },

  // Pelvis Obgyn Body Parts Hierarchy
  getPelvisObgynBodyPartsHierarchy() {
    return api.get('mocks/pelvis-obgyn/body-parts-hierarchy/hierarchy/');
  },

  getPelvisObgynBodyParts() {
    return api.get('mocks/pelvis-obgyn/body-parts-hierarchy/');
  },

  createPelvisObgynBodyPart(data) {
    return api.post('mocks/pelvis-obgyn/body-parts-hierarchy/', data);
  },

  updatePelvisObgynBodyPart(id, data) {
    return api.put(`mocks/pelvis-obgyn/body-parts-hierarchy/${id}/`, data);
  },

  deletePelvisObgynBodyPart(id) {
    return api.delete(`mocks/pelvis-obgyn/body-parts-hierarchy/${id}/`);
  },

  populateDefaultPelvisObgynBodyParts() {
    return api.post('mocks/pelvis-obgyn/body-parts-hierarchy/populate_defaults/');
  },

  // Pelvis Obgyn Questions endpoints - pediatrics
  getPelvisObgynQuestions(stationId) {
    return api.get(`mocks/pelvis-obgyn/stations/${stationId}/questions/`);
  },

  submitPelvisObgynAnswers(stationId, data) {
    return api.post(`mocks/pelvis-obgyn/stations/${stationId}/submit-answers/`, data);
  },

  getPelvisObgynStations(params = {}) {
    return api.get('mocks/pelvis-obgyn/stations/', { params });
  },













  // Gravid Obgyn Station Folder endpoints 
  getGravidObgynFolders(params = {}) {
    return api.get('mocks/gravid-obgyn/folders/', { params });
  },
  getGravidObgynFoldersBySpecialty(specialty) {
    return api.get(`mocks/gravid-obgyn/folders/`);
  },
  createGravidObgynFolder(data) {
    return api.post('mocks/gravid-obgyn/folders/', data);
  },
  updateGravidObgynFolder(id, data) {
    return api.patch(`mocks/gravid-obgyn/folders/${id}/`, data);
  },
  deleteGravidObgynFolder(id) {
    return api.delete(`mocks/gravid-obgyn/folders/${id}/`);
  },

  // Gravid Obgyn Station endpoints
  getGravidObgynStations(params = {}) {
    return api.get('mocks/gravid-obgyn/stations/', { params });
  },
  getGravidObgynStation(id) {
    return api.get(`mocks/gravid-obgyn/stations/${id}/`);
  },
  createGravidObgynStation(data) {
    return api.post('mocks/gravid-obgyn/stations/', data);
  },
  updateGravidObgynStation(id, data) {
    return api.put(`mocks/gravid-obgyn/stations/${id}/`, data);
  },
  deleteGravidObgynStation(id) {
    return api.delete(`mocks/gravid-obgyn/stations/${id}/`);
  },
  duplicateGravidObgynStation(id) {
    return api.post(`mocks/gravid-obgyn/stations/${id}/duplicate/`);
  },

  // Gravid Obgyn Body Parts Hierarchy
  getGravidObgynBodyPartsHierarchy() {
    return api.get('mocks/gravid-obgyn/body-parts-hierarchy/hierarchy/');
  },

  getGravidObgynBodyParts() {
    return api.get('mocks/gravid-obgyn/body-parts-hierarchy/');
  },

  createGravidObgynBodyPart(data) {
    return api.post('mocks/gravid-obgyn/body-parts-hierarchy/', data);
  },

  updateGravidObgynBodyPart(id, data) {
    return api.put(`mocks/gravid-obgyn/body-parts-hierarchy/${id}/`, data);
  },

  deleteGravidObgynBodyPart(id) {
    return api.delete(`mocks/gravid-obgyn/body-parts-hierarchy/${id}/`);
  },

  populateDefaultGravidObgynBodyParts() {
    return api.post('mocks/gravid-obgyn/body-parts-hierarchy/populate_defaults/');
  },

  // Gravid Obgyn Questions endpoints - pediatrics
  getGravidObgynQuestions(stationId) {
    return api.get(`mocks/gravid-obgyn/stations/${stationId}/questions/`);
  },

  submitGravidObgynAnswers(stationId, data) {
    return api.post(`mocks/gravid-obgyn/stations/${stationId}/submit-answers/`, data);
  },

  getGravidObgynStations(params = {}) {
    return api.get('mocks/gravid-obgyn/stations/', { params });
  },























  // Gyne Obgyn Station Folder endpoints - Pediatrics
  getGyneObgynFolders(params = {}) {
    return api.get('mocks/gyne-obgyn/folders/', { params });
  },
  getGyneObgynFoldersBySpecialty(specialty) {
    return api.get(`mocks/gyne-obgyn/folders/`);
  },
  createGyneObgynFolder(data) {
    return api.post('mocks/gyne-obgyn/folders/', data);
  },
  updateGyneObgynFolder(id, data) {
    return api.patch(`mocks/gyne-obgyn/folders/${id}/`, data);
  },
  deleteGyneObgynFolder(id) {
    return api.delete(`mocks/gyne-obgyn/folders/${id}/`);
  },

  // Gyne Obgyn Station endpoints
  getGyneObgynStations(params = {}) {
    return api.get('mocks/gyne-obgyn/stations/', { params });
  },
  getGyneObgynStation(id) {
    return api.get(`mocks/gyne-obgyn/stations/${id}/`);
  },
  createGyneObgynStation(data) {
    return api.post('mocks/gyne-obgyn/stations/', data);
  },
  updateGyneObgynStation(id, data) {
    return api.put(`mocks/gyne-obgyn/stations/${id}/`, data);
  },
  deleteGyneObgynStation(id) {
    return api.delete(`mocks/gyne-obgyn/stations/${id}/`);
  },
  duplicateGyneObgynStation(id) {
    return api.post(`mocks/gyne-obgyn/stations/${id}/duplicate/`);
  },

  // Gyne Obgyn Body Parts Hierarchy
  getGyneObgynBodyPartsHierarchy() {
    return api.get('mocks/gyne-obgyn/body-parts-hierarchy/hierarchy/');
  },

  getGyneObgynBodyParts() {
    return api.get('mocks/gyne-obgyn/body-parts-hierarchy/');
  },

  createGyneObgynBodyPart(data) {
    return api.post('mocks/gyne-obgyn/body-parts-hierarchy/', data);
  },

  updateGyneObgynBodyPart(id, data) {
    return api.put(`mocks/gyne-obgyn/body-parts-hierarchy/${id}/`, data);
  },

  deleteGyneObgynBodyPart(id) {
    return api.delete(`mocks/gyne-obgyn/body-parts-hierarchy/${id}/`);
  },

  populateDefaultGyneObgynBodyParts() {
    return api.post('mocks/gyne-obgyn/body-parts-hierarchy/populate_defaults/');
  },

  // Gyne Obgyn Questions endpoints - pediatrics
  getGyneObgynQuestions(stationId) {
    return api.get(`mocks/gyne-obgyn/stations/${stationId}/questions/`);
  },

  submitGyneObgynAnswers(stationId, data) {
    return api.post(`mocks/gyne-obgyn/stations/${stationId}/submit-answers/`, data);
  },

  getGyneObgynStations(params = {}) {
    return api.get('mocks/gyne-obgyn/stations/', { params });
  },







  






































};